/* eslint-disable max-lines */
import { escapeHTML, scrollToTop, toDomesticTel, trackClick, isAiTravel } from '@this/src/util';
import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import _ from 'lodash';
import type { ImageFile } from 'react-dropzone';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import type { Moment } from '@this/lib/moment';
import moment from '@this/lib/moment';
import { useAITTheme } from '@this/src/components/shared/ui/theme/provider';
import PersonIcon from '@material-ui/icons/Person';
import LinkButton from '@this/shared/atoms/link_button';
import { Button } from '@this/shared/ui/inputs/button';
import { buildBeforeBakurakuWorkflowRequest } from '@this/domain/bakuraku_workflow_request';
import type BakurakuWorkflowRequest from '@this/domain/bakuraku_workflow_request';

import type ExicCard from '@this/domain/exic_card2';
import type { Gender, ShinkansenSeatType, ShinkansenTicketType } from '@this/domain/traveler_information';
import TravelerInformation from '@this/domain/traveler_information';
import { rentalCarSelectableMinDate } from '@this/components/shared/checker/rental_car_limit';
import { isWithinSixMonth, isOverDate } from '@this/shared/checker/date_checker';
import { compareApproveStage } from '@this/domain/approver/approver';
import { convertHotelToRestrictedHotel } from '@this/src/api/trips/restricted_hotel_plan_log_api';

import { observer } from 'mobx-react';
import type ReserveConfirmStore from '@this/src/domain/reserve_confirm_store';
import {
  StyledAlertTitle,
  StyledDescriptionButton,
  StyledReserveErrorAlert,
  StyledReserveSuccessAlert
} from '@this/src/components/shared/reserve_ui_components';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Flex } from '@this/src/components/shared/ui/layout/flex';
import { Text } from '@this/shared/ui/data_displays/typography/text';
import { InfoOutlined } from '@material-ui/icons';
import { TravelerFromMember } from '@this/src/components/shared/search/search_travelers_block/search_travelers_block';
import DateSelector from '@this/src/components/shared/date_selector/date_selector';
import SelectNationalities from '@this/src/components/shared/nationality/select_nationalities';
import { Radio, RadioGroup } from '@this/src/components/shared/ui/inputs/radio';
import { Select } from '@this/shared/ui/inputs/select';
import Tooltip from '@this/src/components/shared/tooltip/tooltip';
import { AutocompleteSelect } from '@this/src/components/shared/ui/inputs/autocomplete';
import CoreAppContext from '@this/src/components/core_app/core_app_context';
import type { ShinkansenAddressType } from '@this/domain/reserve_info2';
import { businessEkinetUnsupportedTrainTooltip } from '@this/domain/business_ekinet_supported_train';
import { Datepicker } from '@this/src/components/shared/ui/inputs/datepicker';
import AutoCompletableInput from '@this/src/components/shared/autocompletable_input/autocompletable_input';
import NotifiedUserSelectorTemplate from '@this/src/components/shared/user_selector/notified_user_selector.template';
import Markdown from '@this/src/components/shared/markdown/markdown';
import SelectApproveItemList from '@this/src/components/shared/select_approveitemlist/select_approveitemlist';
import type ItemList from '@this/src/domain/approve_item/item_list';
import type ApproveItem from '@this/src/domain/approve_item/approve_item2';
import ApproveRoute from '@this/src/components/shared/approve_route/approve_route_input';
import type { HotelBedType } from '@this/src/domain/hotel/hotel2';
import { Link } from '@this/shared/ui/navigations/link';
import TripRuleErrorSection from './trip_rule_section/trip_rule_error_section';
import TripRuleSuccessSection from './trip_rule_section/trip_rule_success_section';
import TripDuplicationSection from './trip_duplication_section/trip_duplication_section';
import RestrictedHotelSection from './restricted_hotel_section/restricted_hotel_section';
import SelectTravelers from '../../select_travelers/select_travelers';
import SelectDepartments from './select_departments/select_departments';
import SelectOrganizationBases from './select_organization_bases/select_organization_bases';
import ChargingDepartmentSection from './charging_department_section/charging_department_section';
import ProjectNameSection from './project_name_section/project_name_section';
import ExpensesAccountTypeSection from './expenses_account_type_section/expenses_account_type_section';
import ApproveItemDropzone from './approve_items/approve_item_dropzone';
import ApproveItemFilePreview from '@this/components/shared/approve_item_file_preview/approve_item_file_preview';
import WelfareSection from './welfare_section/welfare_section';
import PaymentSection from './payment_section/payment_section';
import PickupStationSection, { PickupStationNotice } from './pickup_station_section/pickup_station_section';

const WORKFLOW_FORM_WIDTH = '420px';
const approveItemInputAreaWithDescriptionStyle: React.CSSProperties = {
  flexDirection: 'column',
  alignItems: 'stretch'
};
const approveItemDescriptionCodeBlockStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  fontFamily: 'monospace',
  color: '#24292f',
  fontSize: '13px',
  lineHeight: '1.5',
  backgroundColor: '#f6f8fa',
  border: '1px solid #d0d7de',
  borderRadius: '6px',
  padding: '12px 16px',
  marginTop: '8px'
};

export const parse = (text: string) => {
  const safeText = escapeHTML(text);

  let result = safeText.replace(/\[(.+)\]\((.+)\)/gm, '<a href="$2" target="_blank">$1</a>');
  result = result.replace(/\*\*(.+)\*\*/, '<span class="bold">$1</span>');
  result = result.replace(/\n/g, '<br />');
  return result;
};

export type Props = {
  store: ReserveConfirmStore;
  isSimpleRequest?: boolean;
  handleBackToSelectClick: () => void;
  handleSaveDraft?: () => void;
  isOnTripsConfirm: boolean;
  handleValidationSuccess?: () => void;
};
const StylesContext = React.createContext<ReturnType<typeof getStyles> | null>(null);

const useStylesContext = () => {
  const styles = useContext(StylesContext);
  if (!styles) {
    throw new Error('StylesContext is not provided');
  }
  return styles;
};

const isOwnTraveler = (traveler: TravelerInformation, store: ReserveConfirmStore) =>
  traveler.userId === store.reserveInfo.loginUserId;

const InputCustomerInformation: React.FC<Props> = ({
  store,
  isSimpleRequest,
  handleBackToSelectClick,
  handleSaveDraft,
  isOnTripsConfirm,
  handleValidationSuccess
}) => {
  const { serviceId } = useContext(CoreAppContext);
  const [showAlert, setShowAlert] = useState(false);
  const [bakurakuWorkflowLoading, setBakurakuWorkflowLoading] = useState(false);
  const [bakurakuWorkflowStatus, setBakurakuWorkflowStatus] = useState('');
  const [bakurakuWorkflowStatusOptions, setBakurakuWorkflowStatusOptions] = useState<string[]>([]);
  const [bakurakuWorkflowActualCount, setBakurakuWorkflowActualCount] = useState(0);
  const [bakurakuWorkflowFetchError, setBakurakuWorkflowFetchError] = useState<string | null>(null);
  const [bakurakuWorkflowSelectorKey, setBakurakuWorkflowSelectorKey] = useState(0);
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const validationErrors: { [key: string]: string | undefined } = store.reserveInfo.validationErrors;
  const isBusinessEkinetDisabled = !store.reserveInfo.isBusinessEkinetSupportedByTrains;
  const businessEkinetDisabledTooltip = businessEkinetUnsupportedTrainTooltip();
  const requiresFreeeWorkflowConnection =
    !!store.reserveInfo.user.organization?.freeeWorkflow &&
    !!store.reserveInfo.user.freeeAuthUri &&
    store.reserveInfo.isApprovalRequired;
  const syncHotelsFromTravelers = useCallback(() => {
    if (store.reserveInfo.reservingTrip.isHotelInfoRequired) {
      store.reserveInfo.syncHotelsFromTravelers();
    }
  }, [store.reserveInfo]);

  const loadBakurakuWorkflowRequests = useCallback(
    async (keyword?: string) => {
      if (!store.reserveInfo.showBakurakuWorkflowSection) {
        return [];
      }

      setBakurakuWorkflowLoading(true);
      setBakurakuWorkflowFetchError(null);

      try {
        const requests = await store.reserveInfo.searchBakurakuWorkflowRequests({
          travelerEmails: store.reserveInfo.bakurakuTargetTravelerEmails(),
          keyword,
          requestStatus: bakurakuWorkflowStatus || undefined
        });
        const statusOptions = Array.from(
          new Set(requests.map(request => request.status).filter(Boolean) as string[])
        );

        setBakurakuWorkflowStatusOptions(prevStatusOptions =>
          bakurakuWorkflowStatus && !statusOptions.includes(bakurakuWorkflowStatus)
            ? [bakurakuWorkflowStatus, ...statusOptions]
            : statusOptions
        );
        setBakurakuWorkflowActualCount(requests.length);
        setBakurakuWorkflowFetchError(null);

        return [buildBeforeBakurakuWorkflowRequest(), ...requests];
      } catch (e) {
        setBakurakuWorkflowActualCount(0);
        setBakurakuWorkflowFetchError('Bakuraku申請の取得に失敗しました。');
        reportError(e as Error);

        return [buildBeforeBakurakuWorkflowRequest()];
      } finally {
        setBakurakuWorkflowLoading(false);
      }
    },
    [bakurakuWorkflowStatus, store.reserveInfo]
  );

  const handleBakurakuWorkflowTravelerChange = useCallback(() => {
    const selectedRequest = store.reserveInfo.bakurakuWorkflowRequest;
    const bakurakuTargetTravelerEmails = store.reserveInfo.bakurakuTargetTravelerEmails();

    if (
      selectedRequest &&
      !selectedRequest.isBeforeApplication() &&
      selectedRequest.requestUserEmail &&
      store.reserveInfo.user.email !== selectedRequest.requestUserEmail &&
      !bakurakuTargetTravelerEmails.includes(selectedRequest.requestUserEmail)
    ) {
      store.reserveInfo.setBakurakuWorkflowRequest(null);
    }

    setBakurakuWorkflowSelectorKey(value => value + 1);
    void loadBakurakuWorkflowRequests();
  }, [loadBakurakuWorkflowRequests, store.reserveInfo]);

  const handleBakurakuWorkflowStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setBakurakuWorkflowStatus(e.target.value);
    setBakurakuWorkflowSelectorKey(value => value + 1);
  }, []);

  const handleBakurakuWorkflowRequestSelect = useCallback(
    (_event: unknown, request: BakurakuWorkflowRequest | null) => {
      store.reserveInfo.setBakurakuWorkflowRequest(request);
    },
    [store.reserveInfo]
  );

  useEffect(() => {
    if (store.reserveInfo.showBakurakuWorkflowSection) {
      void loadBakurakuWorkflowRequests();
    }
  }, [loadBakurakuWorkflowRequests, store.reserveInfo.showBakurakuWorkflowSection]);

  useEffect(() => {
    if (!isBusinessEkinetDisabled) {
      return;
    }
    if (store.reserveInfo.ticket === 'business_ekinet') {
      store.reserveInfo.ticket = 'normal';
    }
    store.reserveInfo.reservingTrip.travelers.forEach(traveler => {
      if (traveler.shinkansenTicketType === 'business_ekinet') {
        traveler.shinkansenTicketType = 'normal';
      }
    });
  }, [isBusinessEkinetDisabled, store.reserveInfo]);

  const handleTravelerTypeChange = useCallback(
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const previousTraveler = store.reserveInfo.reservingTrip.travelers[i];
      if (e.target.value === 'self') {
        const t = new TravelerInformation({});
        t.type = 'self';
        t.assignFromUser(store.reserveInfo.user);
        store.reserveInfo.applyShinkansenFieldsAfterTravelerReplace(t, previousTraveler);
        store.reserveInfo.reservingTrip.travelers[i] = t;
      } else if (e.target.value === 'member') {
        store.reserveInfo.reservingTrip.travelers[i].type = e.target.value;
      } else if (e.target.value === 'companion') {
        const t = new TravelerInformation({});
        t.type = e.target.value;
        store.reserveInfo.applyShinkansenFieldsAfterTravelerReplace(t, previousTraveler);
        store.reserveInfo.reservingTrip.travelers[i] = t;
      }
      handleBakurakuWorkflowTravelerChange();
      syncHotelsFromTravelers();
    },
    [handleBakurakuWorkflowTravelerChange, store, syncHotelsFromTravelers]
  );

  const handleTravelerSelect = useCallback(
    (i: number, traveler: TravelerInformation) => {
      const previousTraveler = store.reserveInfo.reservingTrip.travelers[i];
      store.reserveInfo.applyShinkansenFieldsAfterTravelerReplace(traveler, previousTraveler);
      store.reserveInfo.reservingTrip.travelers[i] = traveler;
      if (i === 0) {
        store.reserveInfo.reservingTrip.travelers[i].firstNameKana = traveler.firstNameKana;
        store.reserveInfo.reservingTrip.travelers[i].lastNameKana = traveler.lastNameKana;
      }
      handleBakurakuWorkflowTravelerChange();
      syncHotelsFromTravelers();
    },
    [handleBakurakuWorkflowTravelerChange, store.reserveInfo, syncHotelsFromTravelers]
  );

  // TODO: reserve_info2 が詳細表示 state と mapping state を一元管理するように寄せる。
  // ChargingDepartmentSection / ProjectNameSection / ExpensesAccountTypeSection の local state をなくし、
  // 開閉状態と detail mode と mapping の source of truth を reserve_info2 に一本化する。
  // 以前はレンタカー操作のたびに全 section の mapping を再初期化していたため、
  // 費用負担部署詳細を開いていないのに showDepartmentDetail が true になり、
  // rental_car の order_item_mappings から charging_department_id が落ちていた。
  // レンタカー操作で更新が必要なのは表示中の詳細 section だけに絞る。
  const syncDetailMappingsForRentalCars = useCallback(() => {
    if (store.reserveInfo.showDepartmentDetail) {
      store.reserveInfo.initOrderItemMappings('department');
    }
    if (store.reserveInfo.showProjectDetail) {
      store.reserveInfo.initOrderItemMappings('project');
    }
    if (store.reserveInfo.showExpensesAccountTypeDetail) {
      store.reserveInfo.initOrderItemMappings('expenses_account_type');
    }
  }, [store.reserveInfo]);

  const handleRentalCarInfoAdd = useCallback(() => {
    store.reserveInfo.reservingTrip.addRentalCar();
    syncDetailMappingsForRentalCars();
  }, [store.reserveInfo, syncDetailMappingsForRentalCars]);

  const handleRentalCarInfoRemove = useCallback(
    (i: number) => (_e: React.MouseEvent<HTMLElement>) => {
      store.reserveInfo.reservingTrip.removeRentalCar(i);
      syncDetailMappingsForRentalCars();
    },
    [store.reserveInfo, syncDetailMappingsForRentalCars]
  );

  const handleApproveCalendarSelect = useCallback(
    (id: number) => (date: Date | null) => {
      store.reserveInfo.approveItemValues.set(id, date ? moment(date).format('YYYY-MM-DD') : '');
    },
    [store.reserveInfo]
  );

  const handleApproveListChange = useCallback(
    (id: number) => (itemList: ItemList | null) => {
      store.reserveInfo.approveItemValueCodes.set(id, itemList?.code || '');
      store.reserveInfo.approveItemValues.set(id, itemList?.name || '');
    },
    [store.reserveInfo]
  );

  const handleApproveFileChange = useCallback(
    (id: number) => async (file: ImageFile) => {
      await store.reserveInfo.setApproveItemFile(id, file);
    },
    [store.reserveInfo]
  );

  const handleApproveFileRemove = useCallback(
    (id: number) => () => {
      store.reserveInfo.approveItemFiles.delete(id);
      store.reserveInfo.approveItemFilesBase64.delete(id);
      store.reserveInfo.approveItemValues.delete(id);
    },
    [store.reserveInfo]
  );

  const validateInput = useCallback(
    (e: React.MouseEvent) => {
      trackClick('/reserve_confirm#1 next page button');
      store.reserveInfo.showValidationErrors = true;
      if (_.isEmpty(store.reserveInfo.validationErrors)) {
        if (isOnTripsConfirm && handleValidationSuccess) {
          e.preventDefault();
          handleValidationSuccess();
        } else {
          store.stepIndex = 1;
          scrollToTop();
        }
      } else {
        e.preventDefault();
      }
    },
    [handleValidationSuccess, isOnTripsConfirm, store]
  );

  const restrictedHotels = useMemo(
    () => store?.reserveInfo?.reservingTrip.groupedRestrictedHotels,
    [store?.reserveInfo?.reservingTrip]
  );

  return (
    <StylesContext.Provider value={styles}>
      {!isSimpleRequest && <Title data-testid="reserve-confirm-input-title">予約情報を入力してください</Title>}
      {store?.queries?.searchType !== 'multipleLocations' &&
        store?.tripRuleErrors?.length > 0 &&
        store.tripRuleErrors.length > 0 && (
          <StyledReserveErrorAlert
            icon={<ErrorOutlineIcon />}
            variant="filled"
            severity="error"
            data-testid="reserve-confirm-input-alert-trip-rule-error"
          >
            <StyledAlertTitle>※{store.tripRuleErrors.length}件の規程違反があります</StyledAlertTitle>
            <Flex>
              <div>
                {store.tripRuleErrors.map(t => (
                  <TripRuleErrorSection
                    key={`trip_rule_error_${t.code}_${t.detail?.lines?.length || 'default'}`}
                    tripRuleError={t}
                    showAlert={showAlert}
                  />
                ))}
              </div>
              {store.tripRuleErrors.some(t => !_.isEmpty(t.detail.lines)) && (
                <StyledDescriptionButton onClick={() => setShowAlert(!showAlert)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="15"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="#666666"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d={showAlert ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
                  </svg>
                </StyledDescriptionButton>
              )}
            </Flex>
          </StyledReserveErrorAlert>
        )}
      {store?.queries?.searchType !== 'multipleLocations' &&
        store?.tripRuleErrors?.length === 0 &&
        store?.tripDistanceAndTimeInfo?.length > 0 && (
          <StyledReserveSuccessAlert
            icon={<InfoOutlined />}
            variant="filled"
            severity="info"
            data-testid="reserve-confirm-input-alert-trip-rule-success"
          >
            <StyledAlertTitle>※旅費規程の範囲内です</StyledAlertTitle>
            <Flex>
              <div>
                {store.tripDistanceAndTimeInfo.map(info => (
                  <TripRuleSuccessSection
                    key={`trip_rule_success_${info.code}_${info.detail?.lines?.length || 'default'}`}
                    tripRuleDistanceTimeInfo={info}
                    showAlert={showAlert}
                  />
                ))}
              </div>
              {store.tripDistanceAndTimeInfo[0].detail.lines?.length && (
                <StyledDescriptionButton onClick={() => setShowAlert(!showAlert)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="15"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="#666666"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d={showAlert ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
                  </svg>
                </StyledDescriptionButton>
              )}
            </Flex>
          </StyledReserveSuccessAlert>
        )}
      {store?.reserveInfo?.tripDuplications?.length ? (
        <>
          {store?.reserveInfo?.tripDuplications?.map((duplication, i) => (
            <TripDuplicationSection
              key={`trip_duplication_${duplication.tripId}_${duplication.userName}`}
              tripDuplication={duplication}
              apiProps={{
                sendLog: i < 1,
                searchQueryId: store?.reserveInfo?.searchQueryId,
                path: '/reserve_confirm',
                tripDuplications: store?.reserveInfo?.tripDuplications
              }}
            />
          ))}
          <small>
            ※代理のお手配の場合、権限の都合で上記のリンクを開けない場合があります。その場合は出張者ご本人に詳細をご確認ください。
          </small>
        </>
      ) : null}
      {restrictedHotels && (
        <div>
          {Object.values(restrictedHotels).map((hotelGroup, index) => (
            <RestrictedHotelSection
              key={`restricted_hotel_group_${hotelGroup[0]?.name || 'unknown'}_${hotelGroup.length}`}
              restrictedHotels={hotelGroup}
              apiProps={{
                sendLog: index < 1,
                searchQueryId: store?.reserveInfo?.searchQueryId,
                restrictedHotels: hotelGroup.map(hotel => convertHotelToRestrictedHotel(hotel))
              }}
            />
          ))}
        </div>
      )}
      <ChildWrapper data-testid="reserve-confirm-input-root">
        {!isSimpleRequest && store?.isCustomerInfoRequired && (
          <Section data-testid="reserve-confirm-input-section-traveler">
            <Subtitle data-testid="reserve-confirm-input-subtitle-traveler">出張者</Subtitle>
            <ChildContent data-testid="reserve-confirm-input-content-traveler">
              {store?.reserveInfo?.reservingTrip.travelers.map((traveler, i) => (
                <div key={`input_traveler_${i}_${traveler.user?.email || traveler.name || i}`}>
                  <TravelerPeopleNum>{i + 1}人目</TravelerPeopleNum>
                  {i === 0 ? (
                    <ReserveConfirmTraveler className="input-customer-information__traveler">
                      <p>
                        {traveler.user?.department?.name && <>{traveler.user.department.name}&nbsp;</>}
                        {traveler.type === 'companion'
                          ? `${traveler.travelerName}`
                          : `${traveler.name} (${traveler.user?.email})`}
                        {traveler.user?.employeeNumber && <>&nbsp;[{traveler.user.employeeNumber}]</>}
                      </p>
                    </ReserveConfirmTraveler>
                  ) : (
                    <ReserveConfirmTraveler className="input-customer-information__traveler">
                      {/* - user=reserveInfo.user */}
                      <TravelerType>
                        <input
                          type="radio"
                          value="self"
                          data-testid={`reserve-confirm-input-traveler-type-self-${i}`}
                          onChange={handleTravelerTypeChange(i)}
                          checked={traveler.type === 'self'}
                        />
                        <span>自分の手配をする</span>
                      </TravelerType>
                      {traveler.type === 'self' && (
                        <TravelerFromMember data-testid={`reserve-confirm-input-traveler-type-self-panel-${i}`}>
                          <SelectTravelersSelectedListItem>
                            <MembersListItemInfo>
                              {store.reserveInfo.user.department?.name && (
                                <MembersListItemName>
                                  {store.reserveInfo.user.department?.name}
                                </MembersListItemName>
                              )}
                              <MembersListItemName>{store.reserveInfo.user.name}</MembersListItemName>
                              <p className="members-list__item__birthday">({store.reserveInfo.user.email})</p>
                              {store.reserveInfo.user.employeeNumber && (
                                <p className="members-list__item__birthday">
                                  [{store.reserveInfo.user.employeeNumber}]
                                </p>
                              )}
                            </MembersListItemInfo>
                          </SelectTravelersSelectedListItem>
                        </TravelerFromMember>
                      )}
                      {store.reserveInfo.user.showOtherMembers && store.reserveInfo.hasMembers && (
                        <>
                          <TravelerType>
                            <input
                              type="radio"
                              value="member"
                              data-testid={`reserve-confirm-input-traveler-type-member-${i}`}
                              onChange={handleTravelerTypeChange(i)}
                              checked={traveler.type === 'member'}
                            />
                            <span>他の人の手配をする</span>
                          </TravelerType>
                          {traveler.type === 'member' && (
                            <TravelerFromMember
                              data-testid={`reserve-confirm-input-traveler-type-member-panel-${i}`}
                            >
                              <SelectTravelers
                                travelers={store.reserveInfo.reservingTrip.travelers}
                                currentIndex={i}
                                onSelect={handleTravelerSelect}
                              />
                            </TravelerFromMember>
                          )}
                        </>
                      )}
                      <TravelerType>
                        <input
                          className="companion-label"
                          type="radio"
                          value="companion"
                          data-testid={`reserve-confirm-input-traveler-type-companion-${i}`}
                          onChange={handleTravelerTypeChange(i)}
                          checked={traveler.type === 'companion'}
                        />
                        <span>手動で入力</span>
                      </TravelerType>
                      {traveler.type === 'companion' && (
                        <TravelerFromMember
                          data-testid={`reserve-confirm-input-traveler-type-companion-panel-${i}`}
                        >
                          <TravelerInputArea>
                            <Label>氏名（ローマ字）</Label>
                            <InputAreaRight>
                              <InputName
                                id="traveler-input-area_english-last-name"
                                className="last-name"
                                type="text"
                                placeholder="例) Yamada"
                                value={traveler.lastNameRoman}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  traveler.lastNameRoman = e.target.value;
                                  traveler.flightLastNameRoman = e.target.value;
                                  syncHotelsFromTravelers();
                                }}
                              />
                              <InputName
                                id="traveler-input-area_english-first-name"
                                className="first-name"
                                type="text"
                                placeholder="例) Ichiro"
                                value={traveler.firstNameRoman}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  traveler.firstNameRoman = e.target.value;
                                  traveler.flightFirstNameRoman = e.target.value;
                                  syncHotelsFromTravelers();
                                }}
                              />
                            </InputAreaRight>
                            {!(
                              _.isEmpty(validationErrors.travelerFirstNameRomen) ||
                              _.isEmpty(validationErrors.travelerLastNameRomen)
                            ) && (
                              <div>
                                <Text color="danger">{validationErrors.travelerFirstNameRomen}</Text>
                                <Text color="danger">{validationErrors.travelerLastNameRomen}</Text>
                              </div>
                            )}
                          </TravelerInputArea>
                          <TravelerInputArea>
                            <Label>氏名（カナ）</Label>
                            <InputAreaRight>
                              <InputName
                                id="traveler-input-area_japanese-last-name"
                                className="last-name"
                                type="text"
                                placeholder="例) ヤマダ"
                                value={traveler.lastNameKana}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  traveler.lastNameKana = e.target.value;
                                  traveler.flightLastNameKana = e.target.value;
                                  syncHotelsFromTravelers();
                                }}
                              />
                              <InputName
                                id="traveler-input-area_japanese-first-name"
                                className="first-name"
                                type="text"
                                placeholder="例) イチロウ"
                                value={traveler.firstNameKana}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  traveler.firstNameKana = e.target.value;
                                  traveler.flightFirstNameKana = e.target.value;
                                  syncHotelsFromTravelers();
                                }}
                              />
                            </InputAreaRight>
                            {!(
                              _.isEmpty(validationErrors.travelerFirstNameKana) ||
                              _.isEmpty(validationErrors.travelerLastNameKana)
                            ) && (
                              <div>
                                <Text color="danger">{validationErrors.travelerFirstNameKana}</Text>
                                <Text color="danger">{validationErrors.travelerLastNameKana}</Text>
                              </div>
                            )}
                          </TravelerInputArea>
                          {store.reserveInfo.reservingTrip.isFlightCustomerInfoRequired && (
                            <>
                              <TravelerInputArea>
                                <Label>誕生日</Label>
                                <InputAreaRight>
                                  <InputBirthDay
                                    id="traveler-input-area_birthday"
                                    type="text"
                                    placeholder="例) 1986-01-01"
                                    value={traveler.birthday?.format('YYYY-MM-DD')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      traveler.birthday = moment(e.target.value);
                                      traveler.flightBirthday = moment(e.target.value);
                                    }}
                                  />
                                </InputAreaRight>
                              </TravelerInputArea>
                              {!_.isEmpty(validationErrors.travelerBirthday) && (
                                <Text color="danger">{validationErrors.travelerBirthday}</Text>
                              )}
                            </>
                          )}
                          {(store.reserveInfo.reservingTrip.isFlightCustomerInfoRequired ||
                            store.reserveInfo.reservingTrip.isHotelInfoRequired) && (
                            <TravelerInputArea>
                              <Label>性別</Label>
                              <InputAreaRight>
                                <RadioLabel>
                                  <input
                                    id="traveler-input-area_radio-male"
                                    className="reserve-confirm__radio-input"
                                    type="radio"
                                    value="male"
                                    checked={traveler.flightGender === 'male'}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      traveler.flightGender = e.target.value as Gender;
                                      syncHotelsFromTravelers();
                                    }}
                                  />
                                  <span>男性</span>
                                </RadioLabel>
                                <RadioLabel>
                                  <input
                                    className="reserve-confirm__radio-input"
                                    type="radio"
                                    value="female"
                                    checked={traveler.flightGender === 'female'}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      traveler.flightGender = e.target.value as Gender;
                                      syncHotelsFromTravelers();
                                    }}
                                  />
                                  <span>女性</span>
                                </RadioLabel>
                              </InputAreaRight>
                            </TravelerInputArea>
                          )}
                        </TravelerFromMember>
                      )}
                    </ReserveConfirmTraveler>
                  )}
                </div>
              ))}
              {!_.isEmpty(validationErrors.selfSelection) && (
                <Text color="danger">{validationErrors.selfSelection}</Text>
              )}
            </ChildContent>
          </Section>
        )}
        {store.reserveInfo.reservingTrip.isFlightCustomerInfoRequired && (
          <Section data-testid="reserve-confirm-input-section-flight">
            <Subtitle data-testid="reserve-confirm-input-subtitle-flight">航空券</Subtitle>
            <ChildContent data-testid="reserve-confirm-input-content-flight">
              {store.reserveInfo.reservingTrip.travelers.map((traveler, i) => (
                <div key={`flight_traveler_${i}_${traveler.user?.email || traveler.name || i}`}>
                  <PeopleNum>{i + 1}人目</PeopleNum>
                  <ReserveConfirmTraveler>
                    <TravelerInputArea>
                      <Label>氏名（カナ）</Label>
                      <InputAreaRight>
                        <InputName
                          type="text"
                          placeholder="例) ヤマダ"
                          data-testid={`reserve-confirm-input-flight-last-name-kana-${i}`}
                          value={traveler.flightLastNameKana}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            traveler.flightLastNameKana = e.target.value;
                          }}
                        />
                        <InputName
                          type="text"
                          placeholder="例) イチロウ"
                          data-testid={`reserve-confirm-input-flight-first-name-kana-${i}`}
                          value={traveler.flightFirstNameKana}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            traveler.flightFirstNameKana = e.target.value;
                          }}
                        />
                      </InputAreaRight>
                      {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired
                        ? !_.isEmpty(store.reserveInfo.validationErrors[`flightCustomerNameKana_${i}`]) && (
                            <Text color="danger">
                              {store.reserveInfo.validationErrors[`flightCustomerNameKana_${i}`]}
                            </Text>
                          )
                        : !_.isEmpty(store.reserveInfo.validationErrors[`flightCustomerName_${i}`]) && (
                            <Text color="danger">
                              {store.reserveInfo.validationErrors[`flightCustomerName_${i}`]}
                            </Text>
                          )}
                    </TravelerInputArea>
                    <TravelerInputArea>
                      {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired ? (
                        <div>
                          <FlightCustomerInfo>
                            <Label>
                              姓（LastName）<Red>*</Red>
                            </Label>
                            <InputName
                              type="text"
                              placeholder="例) Yamada"
                              data-testid={`reserve-confirm-input-flight-last-name-roman-${i}`}
                              value={traveler.flightLastNameRoman}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.flightLastNameRoman = e.target.value;
                              }}
                            />
                          </FlightCustomerInfo>
                          <FlightCustomerInfo>
                            <Label>ミドルネーム（MiddleName）</Label>
                            <InputName
                              type="text"
                              placeholder="例) William"
                              data-testid={`reserve-confirm-input-flight-middle-name-roman-${i}`}
                              value={traveler.flightMiddleNameRoman}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.flightMiddleNameRoman = e.target.value;
                              }}
                            />
                          </FlightCustomerInfo>
                          <FlightCustomerInfo>
                            <Label>
                              名（FirstName）<Red>*</Red>
                            </Label>
                            <InputName
                              type="text"
                              placeholder="例) Ichiro"
                              data-testid={`reserve-confirm-input-flight-first-name-roman-${i}`}
                              value={traveler.flightFirstNameRoman}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.flightFirstNameRoman = e.target.value;
                              }}
                            />
                          </FlightCustomerInfo>
                        </div>
                      ) : (
                        <>
                          <Label>氏名（ローマ字）</Label>
                          <InputAreaRight>
                            <InputName
                              type="text"
                              placeholder="例) Yamada"
                              data-testid={`reserve-confirm-input-flight-last-name-roman-${i}`}
                              value={traveler.flightLastNameRoman}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.flightLastNameRoman = e.target.value;
                              }}
                            />
                            <InputName
                              type="text"
                              placeholder="例) Ichiro"
                              data-testid={`reserve-confirm-input-flight-first-name-roman-${i}`}
                              value={traveler.flightFirstNameRoman}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.flightFirstNameRoman = e.target.value;
                              }}
                            />
                          </InputAreaRight>
                        </>
                      )}
                      {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired
                        ? !_.isEmpty(store.reserveInfo.validationErrors[`flightCustomerName_${i}`]) && (
                            <Text color="danger">
                              {store.reserveInfo.validationErrors[`flightCustomerName_${i}`]}
                            </Text>
                          )
                        : !_.isEmpty(store.reserveInfo.validationErrors[`flightCustomerNameRoman_${i}`]) && (
                            <Text color="danger">
                              {store.reserveInfo.validationErrors[`flightCustomerNameRoman_${i}`]}
                            </Text>
                          )}
                      <Text level="caption">
                        ※1文字でもパスポートの表記と異なるとご搭乗いただけません。
                        <br />
                        ※各航空会社の定めにより予約後の訂正はできないため、キャンセルと新たな予約が必要となります。その場合規定の手数料が発生いたします。
                        {isAiTravel(serviceId) && (
                          <>
                            手数料の詳細については
                            <Link href="/charges" isExternal target="_blank">
                              旅行業務取扱い料金表
                            </Link>
                            をご確認ください。
                          </>
                        )}
                      </Text>
                    </TravelerInputArea>
                    <TravelerInputArea>
                      <Label>性別</Label>
                      <InputAreaRight>
                        <RadioLabel>
                          <input
                            className="reserve-confirm__radio-input"
                            type="radio"
                            value="male"
                            data-testid={`reserve-confirm-input-flight-gender-male-${i}`}
                            checked={traveler.flightGender === 'male'}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              traveler.flightGender = e.target.value as Gender;
                            }}
                          />
                          <span>男性</span>
                        </RadioLabel>
                        <RadioLabel>
                          <input
                            className="reserve-confirm__radio-input"
                            type="radio"
                            value="female"
                            data-testid={`reserve-confirm-input-flight-gender-female-${i}`}
                            checked={traveler.flightGender === 'female'}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              traveler.flightGender = e.target.value as Gender;
                            }}
                          />
                          <span>女性</span>
                        </RadioLabel>
                      </InputAreaRight>
                      {!_.isEmpty(store.reserveInfo.validationErrors[`flightCustomerGender_${i}`]) && (
                        <Text color="danger">
                          {store.reserveInfo.validationErrors[`flightCustomerGender_${i}`]}
                        </Text>
                      )}
                    </TravelerInputArea>
                    <TravelerInputArea>
                      <Label>誕生日</Label>
                      <InputAreaRight>
                        {traveler.type === 'companion' || isOwnTraveler(traveler, store) ? (
                          <DateSelector
                            date={
                              moment(traveler.flightBirthday).isValid()
                                ? moment(traveler.flightBirthday)
                                : moment([2000])
                            }
                            dataTestIdPrefix={`reserve-confirm-input-flight-birthday-${i}`}
                            onDateChange={(date: Moment) => {
                              traveler.setFlightBirthday(date);
                            }}
                          />
                        ) : (
                          '****-**-**(プライバシー保護のため非表示)'
                        )}
                      </InputAreaRight>
                      {!_.isEmpty(store.reserveInfo.validationErrors[`flightCustomerBirthday_${i}`]) && (
                        <Text color="danger">
                          {store.reserveInfo.validationErrors[`flightCustomerBirthday_${i}`]}
                        </Text>
                      )}
                    </TravelerInputArea>
                    {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired && (
                      <TravelerInputArea>
                        <Label>国籍</Label>
                        <InputNationarityAreaRight>
                          <SelectNationalities
                            dataTestIdPrefix={`reserve-confirm-input-flight-nationality-${i}`}
                            nationalities={store.reserveInfo.nationalities}
                            selectedId={traveler.nationalityId}
                            onSelect={(id: number) => {
                              traveler.nationalityId = id;
                            }}
                          />
                        </InputNationarityAreaRight>
                      </TravelerInputArea>
                    )}
                    {!_.isEmpty(store.reserveInfo.validationErrors.travelerBirthday) && (
                      <Text color="danger">{store.reserveInfo.validationErrors.travelerBirthday}</Text>
                    )}
                    <TravelerInputArea>
                      <Label>緊急連絡先電話番号</Label>
                      <InputAreaRight>
                        <Input
                          type="text"
                          placeholder="例) 090-1111-2222"
                          data-testid={`reserve-confirm-input-flight-emergency-tel-${i}`}
                          value={toDomesticTel(traveler.flightTel || '')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            traveler.flightTel = e.target.value;
                          }}
                          onBlur={traveler.convertFlightTel}
                        />
                      </InputAreaRight>
                      {!_.isEmpty(store.reserveInfo.validationErrors[`flightCustomerTel_${i}`]) && (
                        <Text color="danger">{store.reserveInfo.validationErrors[`flightCustomerTel_${i}`]}</Text>
                      )}
                    </TravelerInputArea>
                    {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired && (
                      <>
                        <TravelerInputArea>
                          <Label>
                            パスポート番号<Red>*</Red>
                          </Label>
                          <InputAreaRight>
                            <Input
                              className="passport-number"
                              type="text"
                              placeholder="例) AA1234567"
                              data-testid={`reserve-confirm-input-flight-passport-number-${i}`}
                              value={traveler.passportNumber ?? ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.passportNumber = e.target.value;
                              }}
                            />
                          </InputAreaRight>
                          {!_.isEmpty(store.reserveInfo.validationErrors[`passportNumber_${i}`]) && (
                            <Text color="danger">{store.reserveInfo.validationErrors[`passportNumber_${i}`]}</Text>
                          )}
                        </TravelerInputArea>
                        <TravelerInputArea>
                          <Label>
                            パスポート有効期限<Red>*</Red>
                          </Label>
                          <InputAreaRight>
                            <Input
                              className="passport-expire"
                              type="text"
                              placeholder="例) 2025-01-01"
                              data-testid={`reserve-confirm-input-flight-passport-expire-${i}`}
                              value={traveler.passportExpire ?? ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.passportExpire = e.target.value;
                              }}
                            />
                          </InputAreaRight>
                          {isWithinSixMonth(traveler.passportExpire) && (
                            <div data-testid={`reserve-confirm-input-flight-passport-warning-near-expire-${i}`}>
                              <Text color="danger">有効期限が近づいてきています。</Text>
                              <Text color="danger">
                                渡航先の国によっては入国時に6ヶ月以上のパスポート残存期間が必要です。
                              </Text>
                              <Text color="danger">不足している場合、出国できない可能性があります。</Text>
                              <Text color="danger">
                                内容を変更される場合、アカウント設定から変更してください。
                              </Text>
                            </div>
                          )}
                          {isOverDate(traveler.passportExpire) && (
                            <div data-testid={`reserve-confirm-input-flight-passport-warning-expired-${i}`}>
                              <Text color="danger">有効期限が切れています。パスポートをご確認ください。</Text>
                              <Text color="danger">
                                内容が変更されている場合、アカウント設定から変更してください。
                              </Text>
                            </div>
                          )}
                          {!_.isEmpty(store.reserveInfo.validationErrors[`passportExpire_${i}`]) && (
                            <Text color="danger">{store.reserveInfo.validationErrors[`passportExpire_${i}`]}</Text>
                          )}
                        </TravelerInputArea>
                        <TravelerInputArea>
                          <Label>パスポート発行国</Label>
                          <InputAreaRight>
                            <Input
                              className="passport-issued-coutry"
                              type="text"
                              placeholder="例) 日本"
                              data-testid={`reserve-confirm-input-flight-passport-issued-country-${i}`}
                              value={traveler.passportIssuedCountry}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.passportIssuedCountry = e.target.value;
                              }}
                            />
                          </InputAreaRight>
                          {!_.isEmpty(store.reserveInfo.validationErrors[`passportIssuedCoutry_${i}`]) && (
                            <Text color="danger">
                              {store.reserveInfo.validationErrors[`passportIssuedCoutry_${i}`]}
                            </Text>
                          )}
                        </TravelerInputArea>
                      </>
                    )}
                    <TravelerInputArea>
                      <Label>座席の希望</Label>
                      <InputAirTicketAreaRight>
                        <Input
                          className="air-seat"
                          type="text"
                          placeholder="(任意)「前方通路側」など"
                          data-testid={`reserve-confirm-input-flight-seat-preference-${i}`}
                          value={traveler.airSeat}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            traveler.airSeat = e.target.value;
                          }}
                        />
                        <CaptionText>
                          ※座席の希望にアップグレード席等の要望を記載いただいてもご対応はできません。
                          <br />
                          ※アップグレード席を希望の方は貴社管理者より表示設定を依頼してください。
                          <br />
                          詳しくは
                          <Link href="https://aitravel.jp/knowledges/000001188" isExternal target="_blank">
                            コチラ
                          </Link>
                        </CaptionText>
                      </InputAirTicketAreaRight>
                    </TravelerInputArea>
                    {!store.reserveInfo.user.organization?.setting?.disableShowMilageNumber && (
                      <>
                        {(store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired ||
                          traveler.type === 'companion') && (
                          <TravelerInputArea>
                            <Label>マイル番号</Label>
                            <InputAreaRight>
                              <Input
                                className="mileage-number"
                                type="text"
                                placeholder="(任意)マイル番号を入力してください"
                                data-testid={`reserve-confirm-input-flight-mileage-number-${i}`}
                                value={traveler.mileageNumber}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  traveler.mileageNumber = e.target.value;
                                }}
                              />
                            </InputAreaRight>
                          </TravelerInputArea>
                        )}
                        {traveler.mileageNumbers && traveler.mileageNumbers.length > 0 && (
                          <TravelerInputArea>
                            <Label>登録済みマイル番号</Label>
                            <InputAreaRight>
                              <div data-testid={`reserve-confirm-input-flight-registered-mileage-${i}`}>
                                {traveler.mileageNumbers.map((mileage, i) => (
                                  <div key={`${mileage.carrier}_${mileage.number || i}`}>
                                    {mileage.carrier}:{' '}
                                    {traveler.type === 'companion' || isOwnTraveler(traveler, store)
                                      ? mileage.number
                                      : '**********(プライバシー保護のため非表示)'}
                                  </div>
                                ))}
                              </div>
                            </InputAreaRight>
                          </TravelerInputArea>
                        )}
                        {store.reserveInfo.reservingTrip.isDomesticFlightCustomerInfoRequired &&
                          traveler.type === 'self' && (
                            <TravelerInputArea>
                              <InputAreaRight>
                                <div>
                                  <MileageCaptionText>
                                    ※マイレージ番号を登録したい場合、
                                    <Link href="/users/edit" isExternal target="_blank">
                                      アカウント設定画面
                                    </Link>
                                    から登録をしてください
                                  </MileageCaptionText>
                                </div>
                              </InputAreaRight>
                            </TravelerInputArea>
                          )}
                      </>
                    )}
                    {store.reserveInfo.user.organization?.immigrationCardEnabled && (
                      <TravelerInputArea>
                        <Label>出入国カード作成代行</Label>
                        <InputAreaRight>
                          <RadioLabel>
                            <input
                              className="reserve-confirm__radio-input"
                              type="radio"
                              value="true"
                              data-testid={`reserve-confirm-input-flight-immigration-card-yes-${i}`}
                              checked={traveler.immigrationCardCreation}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.immigrationCardCreation = e.target.value === 'true';
                              }}
                            />
                            <span>はい</span>
                          </RadioLabel>
                          <RadioLabel>
                            <input
                              className="reserve-confirm__radio-input"
                              type="radio"
                              value="false"
                              data-testid={`reserve-confirm-input-flight-immigration-card-no-${i}`}
                              checked={!traveler.immigrationCardCreation}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                traveler.immigrationCardCreation = e.target.value === 'true';
                              }}
                            />
                            <span>いいえ</span>
                          </RadioLabel>
                        </InputAreaRight>
                      </TravelerInputArea>
                    )}
                    {store.reserveInfo.user.organization?.visaApplicationEnabled && (
                      <>
                        <TravelerInputArea>
                          <Label>ビザ申請代行</Label>
                          <InputAreaRight>
                            <RadioLabel>
                              <input
                                className="reserve-confirm__radio-input"
                                type="radio"
                                value="true"
                                data-testid={`reserve-confirm-input-flight-visa-application-yes-${i}`}
                                checked={traveler.visaApplication}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  traveler.visaApplication = e.target.value === 'true';
                                }}
                              />
                              <span>はい</span>
                            </RadioLabel>
                            <RadioLabel>
                              <input
                                className="reserve-confirm__radio-input"
                                type="radio"
                                value="false"
                                data-testid={`reserve-confirm-input-flight-visa-application-no-${i}`}
                                checked={!traveler.visaApplication}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  traveler.visaApplication = e.target.value === 'true';
                                }}
                              />
                              <span>いいえ</span>
                            </RadioLabel>
                          </InputAreaRight>
                        </TravelerInputArea>
                        <Link href="https://aitravel.jp/knowledges/000001151" isExternal target="_blank">
                          ビザの必要な国
                        </Link>
                      </>
                    )}
                  </ReserveConfirmTraveler>
                </div>
              ))}
              <Text color="danger" level="caption">
                {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired
                  ? '※海外への渡航をご予定の場合、氏名等がパスポートの記載と異なると、当日ご利用いただけない場合がございます。'
                  : '※氏名、誕生日が間違っていると搭乗できない場合がありますのでご注意ください。'}
              </Text>
            </ChildContent>
          </Section>
        )}
        {store.reserveInfo.reservingTrip.isShinkansenInfoRequired && (
          <Section data-testid="reserve-confirm-input-section-shinkansen">
            <Subtitle data-testid="reserve-confirm-input-subtitle-shinkansen">新幹線・特急</Subtitle>
            <div data-testid="reserve-confirm-input-content-shinkansen">
              {store.reserveInfo.reservingTrip.travelers.length > 1 && (
                <RailwayTravelerInputHead>
                  <RadioGroup
                    layout="horizontal"
                    value={`${store.reserveInfo.isSeparateShinkansenType}`}
                    onChange={e => {
                      store.reserveInfo.isSeparateShinkansenType = e.target.value === 'true';
                      store.reserveInfo.clearPickupStationData();
                    }}
                  >
                    <TheRadio
                      value="false"
                      style={{ marginRight: '10px' }}
                      radioInputProps={{ 'data-testid': 'reserve-confirm-input-shinkansen-separate-type-false' }}
                    >
                      全員に同じチケットを適用
                    </TheRadio>
                    <TheRadio
                      value="true"
                      radioInputProps={{ 'data-testid': 'reserve-confirm-input-shinkansen-separate-type-true' }}
                    >
                      個別にチケット選ぶ
                    </TheRadio>
                  </RadioGroup>
                </RailwayTravelerInputHead>
              )}
              <RailwayTravelerInputBox>
                {!store.reserveInfo.isSeparateShinkansenType && (
                  <>
                    {!store.reserveInfo.reservingTrip.isPackage && (
                      <RailwayTravelerInputArea withBorder={false}>
                        <Label>チケットの券種</Label>
                        <InputAreaRight>
                          <div>
                            <RadioGroup
                              layout="horizontal"
                              value={store.reserveInfo.ticket}
                              onChange={e => {
                                store.reserveInfo.ticket = e.target.value as ShinkansenTicketType;
                                store.reserveInfo.reservingTrip.travelers.forEach(traveler => {
                                  traveler.shinkansenTicketType = e.target.value as ShinkansenTicketType;
                                });
                              }}
                            >
                              {store.showEx && (
                                <TheRadio
                                  value="ex"
                                  radioInputProps={{
                                    'data-testid': 'reserve-confirm-input-shinkansen-ticket-type-ex'
                                  }}
                                >
                                  EX-IC
                                </TheRadio>
                              )}
                              {store.showBusinessEkinet && (
                                <BusinessEkinetTicketRadio
                                  disabled={isBusinessEkinetDisabled}
                                  disabledTooltip={businessEkinetDisabledTooltip}
                                  dataTestId="reserve-confirm-input-shinkansen-ticket-type-business-ekinet"
                                />
                              )}
                              {store.useKyuusyuuTicket && (
                                <TheRadio
                                  value="kyuusyuu"
                                  radioInputProps={{
                                    'data-testid': 'reserve-confirm-input-shinkansen-ticket-type-kyuusyuu'
                                  }}
                                >
                                  JR九州きっぷ
                                </TheRadio>
                              )}
                              <TheRadio
                                value="normal"
                                radioInputProps={{
                                  'data-testid': 'reserve-confirm-input-shinkansen-ticket-type-normal'
                                }}
                              >
                                通常きっぷ
                              </TheRadio>
                              {store.useBulkTicket && (
                                <FlexRadio value="bulk_ticket">
                                  <span>回数券</span>
                                  <Tooltip>
                                    「回数券」を選択した場合、日時・座席指定をしていない対象区間の特急・乗車券を送付します。
                                  </Tooltip>
                                </FlexRadio>
                              )}
                            </RadioGroup>
                          </div>
                        </InputAreaRight>
                      </RailwayTravelerInputArea>
                    )}
                    {store.reserveInfo.requiresPickupStationSelection() && (
                      <>
                        <PickupStationSection
                          reserveInfo={store.reserveInfo}
                          stations={store.stations}
                          serviceId={serviceId}
                          onPickupStationChange={selection => {
                            store.reserveInfo.setPickupStationData(
                              selection.searchQueryItemId,
                              {
                                pickupStation: selection.pickupStation,
                                pickupStationId: selection.pickupStationId,
                                pickupService: selection.pickupService,
                                ticketingMethod: selection.ticketingMethod
                              },
                              selection.selectionValue,
                              selection.travelerIndex
                            );
                          }}
                        />
                        {Object.entries(store.reserveInfo.validationErrors)
                          .filter(([key]) => key.startsWith('pickupStation_') || key.startsWith('pickupService_'))
                          .map(([key, message]) =>
                            message ? (
                              <Text key={key} color="danger">
                                {message}
                              </Text>
                            ) : null
                          )}
                      </>
                    )}
                    {!store.reserveInfo.reservingTrip.isPackage && store.reserveInfo.ticket !== 'bulk_ticket' && (
                      <RailwayTravelerInputArea withBorder={false}>
                        <Label>座席</Label>
                        <InputAreaRight>
                          <div>
                            <RadioGroup
                              layout="horizontal"
                              value={store.reserveInfo.seat}
                              onChange={e => {
                                store.reserveInfo.seat = e.target.value as ShinkansenSeatType;
                                store.reserveInfo.reservingTrip.travelers.forEach(traveler => {
                                  traveler.shinkansenSeatType = e.target.value as ShinkansenSeatType;
                                });
                              }}
                            >
                              {store.reserveInfo.isShinkansenFreeSeatAvailable && (
                                <TheRadio
                                  value="free"
                                  radioInputProps={{
                                    'data-testid': 'reserve-confirm-input-shinkansen-seat-type-free'
                                  }}
                                >
                                  自由席
                                </TheRadio>
                              )}
                              {store.reserveInfo.isShinkansenReserveSeatAvailable && (
                                <TheRadio
                                  value="reserve"
                                  radioInputProps={{
                                    'data-testid': 'reserve-confirm-input-shinkansen-seat-type-reserve'
                                  }}
                                >
                                  指定席
                                </TheRadio>
                              )}
                              {store.reserveInfo.isShinkansenGreenSeatAvailable && (
                                <TheRadio
                                  value="green"
                                  radioInputProps={{
                                    'data-testid': 'reserve-confirm-input-shinkansen-seat-type-green'
                                  }}
                                >
                                  グリーン席
                                </TheRadio>
                              )}
                            </RadioGroup>
                          </div>
                        </InputAreaRight>
                      </RailwayTravelerInputArea>
                    )}
                  </>
                )}
                {(store.reserveInfo.seat === 'reserve' ||
                  store.reserveInfo.seat === 'green' ||
                  store.reserveInfo.ticket === 'ex' ||
                  store.reserveInfo.isSeparateShinkansenType) &&
                  store.reserveInfo.reservingTrip.travelers.map((traveler, i) => (
                    <RailwayTravelerInputArea key={`railway_traveler_${traveler.id || i}`} withBorder={i > 0}>
                      <RailwayTravelerInputName
                        className="reserve-confirm__peoplenum"
                        data-testid={`reserve-confirm-input-shinkansen-traveler-name-${i}`}
                      >
                        <ThePersonIcon />
                        {traveler.lastNameRoman} {traveler.firstNameRoman}
                      </RailwayTravelerInputName>
                      <ReserveConfirmTraveler>
                        {store.reserveInfo.isSeparateShinkansenType && (
                          <>
                            <TravelerInputAreaItem>
                              <GrayLabel>チケットの券種</GrayLabel>
                              <div data-testid={`reserve-confirm-input-shinkansen-ticket-type-group-${i}`}>
                                <RadioGroup
                                  layout="horizontal"
                                  value={traveler.shinkansenTicketType}
                                  onChange={e => {
                                    traveler.shinkansenTicketType = e.target.value as ShinkansenTicketType;
                                    if (
                                      e.target.value === 'ex' &&
                                      traveler.shinkansenSeatType !== 'green' &&
                                      traveler.shinkansenSeatType !== 'reserve'
                                    ) {
                                      traveler.shinkansenSeatType = 'reserve';
                                    }
                                  }}
                                >
                                  {store.showEx && !store.reserveInfo.reservingTrip.isPackage && (
                                    <TheRadio
                                      value="ex"
                                      radioInputProps={{
                                        'data-testid': `reserve-confirm-input-shinkansen-ticket-type-${i}-ex`
                                      }}
                                    >
                                      EX-IC
                                    </TheRadio>
                                  )}
                                  {store.showBusinessEkinet && !store.reserveInfo.reservingTrip.isPackage && (
                                    <BusinessEkinetTicketRadio
                                      disabled={isBusinessEkinetDisabled}
                                      disabledTooltip={businessEkinetDisabledTooltip}
                                      dataTestId={`reserve-confirm-input-shinkansen-ticket-type-${i}-business-ekinet`}
                                    />
                                  )}
                                  {store.useKyuusyuuTicket && (
                                    <TheRadio
                                      value="kyuusyuu"
                                      radioInputProps={{
                                        'data-testid': `reserve-confirm-input-shinkansen-ticket-type-${i}-kyuusyuu`
                                      }}
                                    >
                                      JR九州きっぷ
                                    </TheRadio>
                                  )}
                                  <TheRadio
                                    value="normal"
                                    radioInputProps={{
                                      'data-testid': `reserve-confirm-input-shinkansen-ticket-type-${i}-normal`
                                    }}
                                  >
                                    通常きっぷ
                                  </TheRadio>
                                  {store.useBulkTicket && (
                                    <FlexRadio value="bulk_ticket">
                                      <span>回数券</span>
                                      <Tooltip>
                                        「回数券」を選択した場合、日時・座席指定をしていない対象区間の特急・乗車券を送付します。
                                      </Tooltip>
                                    </FlexRadio>
                                  )}
                                </RadioGroup>
                                {!_.isEmpty(store.reserveInfo.validationErrors[`shinkansenTicketType_${i}`]) && (
                                  <Text color="danger">
                                    {store.reserveInfo.validationErrors[`shinkansenTicketType_${i}`]}
                                  </Text>
                                )}
                                {!_.isEmpty(
                                  store.reserveInfo.validationErrors[`travelerBusinessEkinetAccount_${i}`]
                                ) && (
                                  <div style={{ whiteSpace: 'pre-line' }}>
                                    <Text color="danger">
                                      {store.reserveInfo.validationErrors[`travelerBusinessEkinetAccount_${i}`]}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </TravelerInputAreaItem>
                            {store.reserveInfo.travelerRequiresPickupStation(i) && (
                              <>
                                <PickupStationSection
                                  reserveInfo={store.reserveInfo}
                                  stations={store.stations}
                                  serviceId={serviceId}
                                  travelerIndex={
                                    store.reserveInfo.shouldSelectPickupStationPerTraveler() ? i : null
                                  }
                                  onPickupStationChange={selection => {
                                    store.reserveInfo.setPickupStationData(
                                      selection.searchQueryItemId,
                                      {
                                        pickupStation: selection.pickupStation,
                                        pickupStationId: selection.pickupStationId,
                                        pickupService: selection.pickupService,
                                        ticketingMethod: selection.ticketingMethod
                                      },
                                      selection.selectionValue,
                                      selection.travelerIndex
                                    );
                                  }}
                                />
                                {Object.entries(store.reserveInfo.validationErrors)
                                  .filter(([key]) => {
                                    if (!(key.startsWith('pickupStation_') || key.startsWith('pickupService_'))) {
                                      return false;
                                    }
                                    if (!store.reserveInfo.shouldSelectPickupStationPerTraveler()) {
                                      return true;
                                    }
                                    return key.endsWith(`_${i}`);
                                  })
                                  .map(([key, message]) =>
                                    message ? (
                                      <Text key={key} color="danger">
                                        {message}
                                      </Text>
                                    ) : null
                                  )}
                              </>
                            )}
                            {traveler.shinkansenTicketType !== 'bulk_ticket' && (
                              <TravelerInputAreaItem>
                                <GrayLabel>座席</GrayLabel>
                                <div data-testid={`reserve-confirm-input-shinkansen-seat-type-group-${i}`}>
                                  <RadioGroup
                                    layout="horizontal"
                                    value={traveler.shinkansenSeatType}
                                    onChange={e => {
                                      traveler.shinkansenSeatType = e.target.value as ShinkansenSeatType;
                                    }}
                                  >
                                    {store.reserveInfo.isShinkansenFreeSeatAvailable && (
                                      <TheRadio
                                        value="free"
                                        radioInputProps={{
                                          'data-testid': `reserve-confirm-input-shinkansen-seat-type-${i}-free`
                                        }}
                                      >
                                        自由席
                                      </TheRadio>
                                    )}
                                    {store.reserveInfo.isShinkansenReserveSeatAvailable && (
                                      <TheRadio
                                        value="reserve"
                                        radioInputProps={{
                                          'data-testid': `reserve-confirm-input-shinkansen-seat-type-${i}-reserve`
                                        }}
                                      >
                                        指定席
                                      </TheRadio>
                                    )}
                                    {store.reserveInfo.isShinkansenGreenSeatAvailable && (
                                      <TheRadio
                                        value="green"
                                        radioInputProps={{
                                          'data-testid': `reserve-confirm-input-shinkansen-seat-type-${i}-green`
                                        }}
                                      >
                                        グリーン席
                                      </TheRadio>
                                    )}
                                  </RadioGroup>
                                  {!_.isEmpty(store.reserveInfo.validationErrors[`shinkansenSeatType_${i}`]) && (
                                    <Text color="danger">
                                      {store.reserveInfo.validationErrors[`shinkansenSeatType_${i}`]}
                                    </Text>
                                  )}
                                </div>
                              </TravelerInputAreaItem>
                            )}
                          </>
                        )}
                        {(traveler.shinkansenSeatType === 'reserve' ||
                          traveler.shinkansenSeatType === 'green' ||
                          (!store.reserveInfo.isSeparateShinkansenType &&
                            (store.reserveInfo.seat === 'reserve' || store.reserveInfo.seat === 'green')) ||
                          store.reserveInfo.ticket === 'ex' ||
                          traveler.shinkansenTicketType === 'ex') && (
                          <TravelerInputAreaItem>
                            <GrayLabel>座席の希望</GrayLabel>
                            <div>
                              <RadioGroup
                                layout="horizontal"
                                value={traveler.shinkansenSeat}
                                onChange={e => {
                                  traveler.shinkansenSeat = e.target.value;
                                }}
                              >
                                <TheRadio
                                  value="指定なし"
                                  radioInputProps={{
                                    'data-testid': `reserve-confirm-input-shinkansen-seat-preference-${i}-none`
                                  }}
                                >
                                  指定なし
                                </TheRadio>
                                <TheRadio
                                  value="窓側"
                                  radioInputProps={{
                                    'data-testid': `reserve-confirm-input-shinkansen-seat-preference-${i}-window`
                                  }}
                                >
                                  窓側
                                </TheRadio>
                                <TheRadio
                                  value="通路側"
                                  radioInputProps={{
                                    'data-testid': `reserve-confirm-input-shinkansen-seat-preference-${i}-aisle`
                                  }}
                                >
                                  通路側
                                </TheRadio>
                                <TheRadio
                                  value="その他"
                                  radioInputProps={{
                                    'data-testid': `reserve-confirm-input-shinkansen-seat-preference-${i}-other`
                                  }}
                                >
                                  その他
                                </TheRadio>
                              </RadioGroup>
                              {traveler.shinkansenSeat === 'その他' && (
                                <Input
                                  data-testid={`reserve-confirm-input-shinkansen-seat-preference-other-input-${i}`}
                                  className="shinkansen-seat"
                                  type="text"
                                  placeholder="(任意)"
                                  style={{ margin: '5px 0 0' }}
                                  value={traveler.shinkansenSeatInfo}
                                  onChange={e => {
                                    traveler.shinkansenSeatInfo = e.target.value;
                                  }}
                                />
                              )}
                            </div>
                          </TravelerInputAreaItem>
                        )}
                        {(store.reserveInfo.isSeparateShinkansenType
                          ? traveler.shinkansenTicketType
                          : store.reserveInfo.ticket) === 'ex' &&
                          (store.reserveInfo.user.organization?.exicUsageMode === 'user_based' ||
                          traveler.type === 'companion' ? (
                            <>
                              <TravelerInputAreaItem>
                                <GrayLabel>EXIC会員ID</GrayLabel>
                                <div>
                                  <Input
                                    data-testid={`reserve-confirm-input-shinkansen-exic-number-${i}`}
                                    type="text"
                                    placeholder="(任意)EXIC会員ID"
                                    style={{ margin: '5px 0 0' }}
                                    value={traveler.exicNumber || ''}
                                    onChange={e => {
                                      traveler.exicNumber = e.target.value;
                                    }}
                                    autoComplete="new-password"
                                  />
                                </div>
                              </TravelerInputAreaItem>
                              <TravelerInputAreaItem>
                                <GrayLabel>EXICパスワード</GrayLabel>
                                <div>
                                  <Input
                                    data-testid={`reserve-confirm-input-shinkansen-exic-password-${i}`}
                                    type={traveler.exicPasswordType}
                                    placeholder="(任意)EXICパスワード"
                                    style={{ margin: '5px 0 0' }}
                                    value={traveler.exicPassword || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      traveler.exicPassword = e.target.value;
                                    }}
                                    autoComplete="new-password"
                                  />
                                  <span
                                    onClick={() => {
                                      traveler.exicPasswordType =
                                        traveler.exicPasswordType === 'password' ? 'text' : 'password';
                                    }}
                                  >
                                    {traveler.exicPasswordType === 'password' ? (
                                      <VisibilityIcon />
                                    ) : (
                                      <VisibilityOffIcon />
                                    )}
                                  </span>
                                </div>
                              </TravelerInputAreaItem>
                            </>
                          ) : store.reserveInfo.user.organization?.exicUsageMode === 'card_based' ? (
                            <TravelerInputAreaItem>
                              <GrayLabel>EXIC会員ID</GrayLabel>
                              <AutocompleteSelect
                                data-testid={`reserve-confirm-input-shinkansen-exic-card-select-${i}`}
                                placeholder="未選択"
                                value={
                                  (store.exicCards.find(o => o.id === traveler?.exicCardId) as ExicCard) ?? null
                                }
                                options={Array.from(store.exicCards ?? [])}
                                getOptionLabel={o => o.exicNumber}
                                onChange={(_e, card) => {
                                  traveler.exicCardId = card?.id;
                                }}
                                disabled={!store.reserveInfo.user.organization?.allowUserExicInput}
                                fullWidth
                              />
                              {!_.isEmpty(store.reserveInfo.validationErrors[`travelerExicCardId_${i}`]) && (
                                <Text color="danger">
                                  {store.reserveInfo.validationErrors[`travelerExicCardId_${i}`]}
                                </Text>
                              )}
                            </TravelerInputAreaItem>
                          ) : null)}
                        {!store.reserveInfo.isSeparateShinkansenType &&
                          !_.isEmpty(store.reserveInfo.validationErrors[`travelerBusinessEkinetAccount_${i}`]) && (
                            <div style={{ whiteSpace: 'pre-line' }}>
                              <Text color="danger">
                                {store.reserveInfo.validationErrors[`travelerBusinessEkinetAccount_${i}`]}
                              </Text>
                            </div>
                          )}
                      </ReserveConfirmTraveler>
                    </RailwayTravelerInputArea>
                  ))}
              </RailwayTravelerInputBox>
              {store.reserveInfo.requiresPickupStationSelection() && (
                <PickupStationNotice perTraveler={store.reserveInfo.shouldSelectPickupStationPerTraveler()} />
              )}
              {!store.reserveInfo.isExOnlyShinkansenTicketing() && (
                <>
                  {store.reserveInfo.user.organization?.useQr && store.reserveInfo.user.organization?.usePostal && (
                    <>
                      <TravelerPeopleNum data-testid="reserve-confirm-input-shinkansen-preferred-ticketing-method-label">
                        発券方法
                      </TravelerPeopleNum>
                      <ReserveConfirmTraveler>
                        <TravelerType>
                          <input
                            id="preferred_ticketing_method_qr"
                            data-testid="reserve-confirm-input-preferred-ticketing-method-qr"
                            className="companion-label"
                            type="radio"
                            value="qr"
                            onChange={e => {
                              store.reserveInfo.preferredTicketingMethod = e.target.value as 'qr' | 'postal';
                            }}
                            checked={store.reserveInfo.preferredTicketingMethod === 'qr'}
                          />
                          <span>QR発券（QRコードで受け取る）</span>
                        </TravelerType>
                        <TravelerType>
                          <input
                            id="preferred_ticketing_method_postal"
                            data-testid="reserve-confirm-input-preferred-ticketing-method-postal"
                            className="companion-label"
                            type="radio"
                            value="postal"
                            onChange={e => {
                              store.reserveInfo.preferredTicketingMethod = e.target.value as 'qr' | 'postal';
                            }}
                            checked={store.reserveInfo.preferredTicketingMethod === 'postal'}
                          />
                          <span>チケット郵送（自宅・職場に届けてもらう）</span>
                        </TravelerType>
                      </ReserveConfirmTraveler>
                    </>
                  )}
                  {store.reserveInfo.reservingTrip.isPackage && (
                    <>
                      <TravelerPeopleNum>
                        ※パッケージ商品の新幹線チケットは日時の変更ができません。通常のチケットとは取扱いが異なりますのでご注意ください
                      </TravelerPeopleNum>
                      <p>
                        <br />
                      </p>
                    </>
                  )}
                  <TravelerPeopleNum data-testid="reserve-confirm-input-shinkansen-address-label">
                    切符送り先
                  </TravelerPeopleNum>
                  <ReserveConfirmTraveler>
                    {store.reserveInfo.isShinkansenQrTicketing() ? (
                      utils.isTabikobo(serviceId) ? (
                        <p data-testid="reserve-confirm-input-shinkansen-qr-guidance">
                          【通常きっぷ】の新幹線チケットにつきましては、予約完了後、チャットにてQRコードを添付いたします。
                          <br />
                          添付したQRコードで駅の指定席券売機もしくは窓口にて発券をお願いいたします。
                          <br />
                          なお、発券方法等の詳細は、チャットにてご案内いたします。
                        </p>
                      ) : (
                        <p data-testid="reserve-confirm-input-shinkansen-qr-guidance">
                          新幹線チケットにつきましては、弊社で予約後、チャット画面にてQRコードを添付いたします。
                          <br />
                          添付したQRコードをもとに駅の指定席券売機で発券をお願いいたします。
                          <br />
                          なお、発券方法等の詳細は、チャットにてご案内いたします。
                        </p>
                      )
                    ) : store.reserveInfo.isShinkansenPostalTicketing() ? (
                      <>
                        {store.reserveInfo.user.organization?.enabledCustomizeAddress && (
                          <>
                            <TravelerType>
                              <input
                                id="shinkansen_text"
                                className="companion-label"
                                type="radio"
                                value="text"
                                onChange={e => {
                                  store.reserveInfo.shinkansenAddressType = e.target
                                    .value as ShinkansenAddressType;
                                }}
                                checked={store.reserveInfo.shinkansenAddressType === 'text'}
                              />
                              <span>手動入力</span>
                            </TravelerType>
                            {store.reserveInfo.shinkansenAddressType === 'text' && (
                              <TravelerFromMember data-testid="reserve-confirm-input-shinkansen-address-type-text">
                                <span>郵便番号</span>
                                <Input
                                  data-testid="reserve-confirm-input-shinkansen-postcode"
                                  id="postcode"
                                  type="text"
                                  name="postcode"
                                  value={store.reserveInfo.postcode}
                                  onChange={e => {
                                    store.reserveInfo.postcode = e.target.value;
                                  }}
                                />
                                <span>住所</span>
                                <input
                                  data-testid="reserve-confirm-input-shinkansen-address"
                                  id="shinkansen_address"
                                  type="text"
                                  placeholder="チケットを送付するのに必要な情報まで記入ください（ビル名、部屋番号など）"
                                  value={store.reserveInfo.shinkansenAddress}
                                  onChange={e => {
                                    store.reserveInfo.shinkansenAddress = e.target.value;
                                  }}
                                />
                                <span>宛名</span>
                                <input
                                  data-testid="reserve-confirm-input-shinkansen-addressee"
                                  id="shinkansen_addressee"
                                  type="text"
                                  placeholder="個人宅への送付の場合は、送付先住所に加え、お受け取りの方のお名前をフルネームで記載をお願いします"
                                  value={store.reserveInfo.shinkansenAddressee}
                                  onChange={e => {
                                    store.reserveInfo.shinkansenAddressee = e.target.value;
                                  }}
                                />
                              </TravelerFromMember>
                            )}
                          </>
                        )}
                        {store.reserveInfo.departments && store.reserveInfo.departments.length > 0 && (
                          <>
                            <TravelerType>
                              <input
                                type="radio"
                                id="shinkansen_select"
                                value="select"
                                onChange={e => {
                                  store.reserveInfo.shinkansenAddressType = e.target
                                    .value as ShinkansenAddressType;
                                }}
                                checked={store.reserveInfo.shinkansenAddressType === 'select'}
                              />
                              <span>部署から選択</span>
                            </TravelerType>
                            {store.reserveInfo.shinkansenAddressType === 'select' && (
                              <TravelerFromMember data-testid="reserve-confirm-input-shinkansen-address-type-select">
                                <SelectDepartments
                                  departments={store.reserveInfo.departments}
                                  onSelect={department => {
                                    store.reserveInfo.organizationAddress = department.organizationAddress;
                                  }}
                                  disabledAddress
                                  user={store.reserveInfo.user}
                                  isRequired
                                  workflowFormWidth={WORKFLOW_FORM_WIDTH}
                                />
                              </TravelerFromMember>
                            )}
                          </>
                        )}
                        {store.organizationBases.length > 0 && (
                          <>
                            <TravelerType>
                              <input
                                type="radio"
                                id="shinkansen_select"
                                value="organization_base_select"
                                onChange={e => {
                                  store.reserveInfo.shinkansenAddressType = e.target
                                    .value as ShinkansenAddressType;
                                }}
                                checked={store.reserveInfo.shinkansenAddressType === 'organization_base_select'}
                              />
                              <span>拠点から選択</span>
                            </TravelerType>
                            {store.reserveInfo.shinkansenAddressType === 'organization_base_select' && (
                              <TravelerFromMember data-testid="reserve-confirm-input-shinkansen-address-type-organization-base-select">
                                <SelectOrganizationBases
                                  organizationBases={store.organizationBases}
                                  onSelect={organizationBase => {
                                    store.reserveInfo.organizationBase = organizationBase;
                                  }}
                                  isRequired
                                />
                              </TravelerFromMember>
                            )}
                          </>
                        )}
                      </>
                    ) : null}
                  </ReserveConfirmTraveler>
                  {store.reserveInfo.validationErrors.postcode && (
                    <Text color="danger">{store.reserveInfo.validationErrors.postcode}</Text>
                  )}
                  {store.reserveInfo.validationErrors.shinkansenAddress && (
                    <Text color="danger">{store.reserveInfo.validationErrors.shinkansenAddress}</Text>
                  )}
                </>
              )}
            </div>
          </Section>
        )}
        {store.reserveInfo.reservingTrip.isHotelInfoRequired && (
          <Section data-testid="reserve-confirm-input-section-hotel">
            <Subtitle data-testid="reserve-confirm-input-subtitle-hotel">宿泊</Subtitle>
            {store.reserveInfo.hotels.map((hotel, i) => (
              <div key={`hotel_${hotel.name}_${i}`}>
                <HotelName>{hotel.name}</HotelName>
                {store.reserveInfo.validationErrors[`autoReserveRakutenHotel_${i}`] && (
                  <div
                    style={{ color: '#d32f2f', fontSize: '13px' }}
                    dangerouslySetInnerHTML={{
                      __html: parse(store.reserveInfo.validationErrors[`autoReserveRakutenHotel_${i}`] || '')
                    }}
                  />
                )}
                {store.reserveInfo.validationErrors[`agodaHotelDuplicate_${i}`] && (
                  <Text color="danger">{store.reserveInfo.validationErrors[`agodaHotelDuplicate_${i}`]}</Text>
                )}
                {hotel.rooms &&
                  hotel.rooms.map((room, j) => (
                    <div key={`hotel_${i}_room_${j}`}>
                      <RoomNum>{j + 1}室目</RoomNum>
                      <ChildContent>
                        <TravelerInputArea>
                          <Label>代表者氏名（カナ）</Label>
                          <InputAreaRight>
                            <InputName
                              type="text"
                              placeholder="例) ヤマダ"
                              data-testid={`reserve-confirm-input-hotel-guest-last-name-kana-${i}-${j}`}
                              value={room.hotelLastNameKana}
                              onChange={e => {
                                room.hotelLastNameKana = e.target.value;
                              }}
                            />
                            <InputName
                              type="text"
                              placeholder="例) イチロウ"
                              data-testid={`reserve-confirm-input-hotel-guest-first-name-kana-${i}-${j}`}
                              value={room.hotelFirstNameKana}
                              onChange={e => {
                                room.hotelFirstNameKana = e.target.value;
                              }}
                            />
                          </InputAreaRight>
                          {store.reserveInfo.validationErrors[`firstNameKana_${j}`] && (
                            <Text color="danger">{store.reserveInfo.validationErrors[`firstNameKana_${j}`]}</Text>
                          )}
                          {store.reserveInfo.validationErrors[`lastNameKana_${j}`] && (
                            <Text color="danger">{store.reserveInfo.validationErrors[`lastNameKana_${j}`]}</Text>
                          )}
                        </TravelerInputArea>
                        <TravelerInputArea>
                          <Label>代表者氏名（ローマ字）</Label>
                          <InputAreaRight>
                            <InputName
                              type="text"
                              placeholder="例) Yamada"
                              data-testid={`reserve-confirm-input-hotel-guest-last-name-roman-${i}-${j}`}
                              value={room.hotelLastName}
                              onChange={e => {
                                room.hotelLastName = e.target.value;
                              }}
                            />
                            <InputName
                              type="text"
                              placeholder="例) Ichiro"
                              data-testid={`reserve-confirm-input-hotel-guest-first-name-roman-${i}-${j}`}
                              value={room.hotelFirstName}
                              onChange={e => {
                                room.hotelFirstName = e.target.value;
                              }}
                            />
                          </InputAreaRight>
                          {store.reserveInfo.validationErrors[`firstName_${j}`] && (
                            <Text color="danger">{store.reserveInfo.validationErrors[`firstName_${j}`]}</Text>
                          )}
                          {store.reserveInfo.validationErrors[`lastName_${j}`] && (
                            <Text color="danger">{store.reserveInfo.validationErrors[`lastName_${j}`]}</Text>
                          )}
                        </TravelerInputArea>
                        <TravelerInputArea>
                          <Label>代表者電話番号</Label>
                          <InputAreaRight>
                            <Input
                              type="text"
                              placeholder="例) 090-1111-2222"
                              data-testid={`reserve-confirm-input-hotel-guest-tel-${i}-${j}`}
                              value={toDomesticTel(room.hotelTel)}
                              onChange={e => {
                                room.hotelTel = e.target.value;
                              }}
                            />
                          </InputAreaRight>
                          {store.reserveInfo.validationErrors[`tel_${j}`] && (
                            <Text color="danger">{store.reserveInfo.validationErrors[`tel_${j}`]}</Text>
                          )}
                        </TravelerInputArea>
                        {room.bedTypes ? (
                          <TravelerInputArea>
                            <Label>ベッドの種類</Label>
                            <InputAreaRight>
                              <div>
                                {renderBedTypeWarning(room.bedTypes)}
                                {room.bedTypes.map((bedType, bedTypeIndex) => (
                                  <p key={`bedtype_${bedType.id || bedType.description}_${bedTypeIndex}`}>
                                    {bedType.description}
                                  </p>
                                ))}
                              </div>
                            </InputAreaRight>
                          </TravelerInputArea>
                        ) : (
                          <TravelerInputArea>
                            <Label>ベッドの種類</Label>
                            <InputAreaRight>
                              <div>
                                <p>-</p>
                              </div>
                            </InputAreaRight>
                          </TravelerInputArea>
                        )}
                      </ChildContent>
                    </div>
                  ))}
              </div>
            ))}
          </Section>
        )}
        {!store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired &&
          store.reserveInfo.rentalCarAvailable &&
          store.fromTripForm === false && (
            <Section data-testid="reserve-confirm-input-section-rental-car">
              <Subtitle data-testid="reserve-confirm-input-subtitle-rental-car">
                <SubtitleLeft>レンタカー</SubtitleLeft>
                <SubtitleRight>※オプション</SubtitleRight>
              </Subtitle>
              <ChildContent data-testid="reserve-confirm-input-content-rental-car">
                <RentalCarDescription>
                  レンタカーをご利用予定の場合、下の「レンタカーを予約する」のテキストをクリックしてください。
                </RentalCarDescription>
                <ReserveConfirmTraveler>
                  <RentalCarLabel>
                    <input
                      data-testid="reserve-confirm-input-rental-car-required-no"
                      className="companion-label"
                      type="radio"
                      value="false"
                      onChange={e => {
                        store.reserveInfo.rentalCarRequired = e.target.value as 'false' | 'true';
                      }}
                      checked={store.reserveInfo.rentalCarRequired === 'false'}
                    />
                    <span>レンタカーを予約しない</span>
                  </RentalCarLabel>
                  <RentalCarLabel>
                    <input
                      data-testid="reserve-confirm-input-rental-car-required-yes"
                      id="book-rental-car"
                      type="radio"
                      value="true"
                      onChange={e => {
                        store.reserveInfo.rentalCarRequired = e.target.value as 'false' | 'true';
                        if (store.reserveInfo.reservingTrip.rentalCars.length === 0) {
                          store.reserveInfo.reservingTrip.addRentalCar();
                        }
                        syncDetailMappingsForRentalCars();
                      }}
                      checked={store.reserveInfo.rentalCarRequired === 'true'}
                    />
                    <span>レンタカーを予約する（※確定料金は依頼後のチャット画面でお伝えします）</span>
                  </RentalCarLabel>
                  {store.reserveInfo.rentalCarRequired === 'true' && (
                    <>
                      {store.reserveInfo.reservingTrip.rentalCars.map((rentalCar, i) => (
                        <div key={`rentalcar_${i}`} data-testid={`reserve-confirm-input-rental-car-item-${i}`}>
                          <RentalCarOrderTitle>ご依頼内容{i !== 0 && i + 1}</RentalCarOrderTitle>
                          <RentalCarOrderContent>
                            {i !== 0 && (
                              <RentalCarRemove
                                data-testid={`reserve-confirm-input-rental-car-remove-${i}`}
                                onClick={handleRentalCarInfoRemove(i)}
                              >
                                ×
                              </RentalCarRemove>
                            )}
                            <TravelerRentalCarInputAreaTitleText>
                              <TravelerRentalCarLabel>出発日時</TravelerRentalCarLabel>
                              <TravelerRentalCarInputAreaTextInput>
                                <RentalCarDatePicker>
                                  <Datepicker
                                    type="datetime-local"
                                    value={rentalCar.departureTime?.toDate()}
                                    onChange={date => {
                                      rentalCar.departureTime = moment(date);
                                    }}
                                    min={rentalCarSelectableMinDate(
                                      moment(),
                                      store.reserveInfo.rentalCarLimitType?.actualDays ?? 2
                                    )}
                                    disabledClear
                                  />
                                </RentalCarDatePicker>
                              </TravelerRentalCarInputAreaTextInput>
                            </TravelerRentalCarInputAreaTitleText>
                            {!_.isEmpty(store.reserveInfo.validationErrors[`departureDate_${i}`]) && (
                              <Text color="danger">
                                {store.reserveInfo.validationErrors[`departureDate_${i}`]}
                              </Text>
                            )}
                            <TravelerRentalCarInputAreaTitleText>
                              <TravelerRentalCarLabel>返却日時</TravelerRentalCarLabel>
                              <TravelerRentalCarInputAreaTextInput>
                                <RentalCarDatePicker>
                                  <Datepicker
                                    type="datetime-local"
                                    value={rentalCar.returnTime?.toDate()}
                                    onChange={date => {
                                      rentalCar.returnTime = moment(date);
                                    }}
                                    min={rentalCarSelectableMinDate(
                                      moment(),
                                      store.reserveInfo.rentalCarLimitType?.actualDays ?? 2
                                    )}
                                    disabledClear
                                  />
                                </RentalCarDatePicker>
                              </TravelerRentalCarInputAreaTextInput>
                            </TravelerRentalCarInputAreaTitleText>
                            <TravelerInputArea>
                              <TravelerRentalCarLabel>出発場所</TravelerRentalCarLabel>
                              <TravelerRentalCarInputAreaTextInput>
                                <AutoCompletableInputBox
                                  data-testid={`reserve-confirm-input-rental-car-departure-place-wrapper-${i}`}
                                >
                                  <StyledAutoCompletableInput
                                    id={`rental-car-departure-place_${i}`}
                                    placeholder="例) 東京駅"
                                    value={rentalCar.departurePlace}
                                    onChange={(_e, value, address) => {
                                      rentalCar.departurePlace = address ? `${value}(${address})` : value;
                                    }}
                                  />
                                </AutoCompletableInputBox>
                              </TravelerRentalCarInputAreaTextInput>
                            </TravelerInputArea>
                            {!_.isEmpty(store.reserveInfo.validationErrors[`departurePlace_${i}`]) && (
                              <Text color="danger">
                                {store.reserveInfo.validationErrors[`departurePlace_${i}`]}
                              </Text>
                            )}
                            <TravelerInputArea>
                              <TravelerRentalCarLabel>返却場所</TravelerRentalCarLabel>
                              <TravelerRentalCarInputArea>
                                <RentalCarRadio>
                                  <input
                                    data-testid={`reserve-confirm-input-rental-car-return-place-current-${i}`}
                                    className="companion-label"
                                    type="radio"
                                    value="current"
                                    onChange={e => {
                                      rentalCar.returnPlace = e.target.value as 'current' | 'other';
                                    }}
                                    checked={rentalCar.returnPlace === 'current'}
                                  />
                                  <RentalCarRadioP>出発場所へ戻す</RentalCarRadioP>
                                </RentalCarRadio>
                                <RentalCarRadio>
                                  <input
                                    data-testid={`reserve-confirm-input-rental-car-return-place-other-${i}`}
                                    type="radio"
                                    value="other"
                                    onChange={e => {
                                      rentalCar.returnPlace = e.target.value as 'current' | 'other';
                                    }}
                                    checked={rentalCar.returnPlace === 'other'}
                                  />
                                  <RentalCarRadioP>
                                    別の場所へ乗り捨てる(※乗り捨てを希望する場所を記載して下さい)
                                  </RentalCarRadioP>
                                </RentalCarRadio>
                                {rentalCar.returnPlace === 'other' && (
                                  <RentalCarRadio>
                                    <AutoCompletableInputBox
                                      data-testid={`reserve-confirm-input-rental-car-return-place-name-wrapper-${i}`}
                                    >
                                      <StyledAutoCompletableInput
                                        id={`rental-car-return-place_${i}`}
                                        value={rentalCar.returnPlaceName}
                                        onChange={(_, value, address) => {
                                          rentalCar.returnPlaceName = address ? `${value}(${address})` : value;
                                        }}
                                      />
                                    </AutoCompletableInputBox>
                                  </RentalCarRadio>
                                )}
                              </TravelerRentalCarInputArea>
                            </TravelerInputArea>
                            {!_.isEmpty(store.reserveInfo.validationErrors[`returnPlace_${i}`]) && (
                              <Text color="danger">{store.reserveInfo.validationErrors[`returnPlace_${i}`]}</Text>
                            )}
                            <TravelerInputArea>
                              <TravelerRentalCarLabel>
                                車種・クラス ※選択いただいたタイプから適切な車両をお選びします。
                              </TravelerRentalCarLabel>
                              <TravelerRentalCarInputAreaCarType>
                                {rentalCar.carTypeOptions.map((c, ci) => (
                                  <RentalCarRadio key={`car_type_${ci}`}>
                                    <input
                                      data-testid={`reserve-confirm-input-rental-car-car-type-${i}-${c[1]}`}
                                      className="companion-label"
                                      type="radio"
                                      value={c[1]}
                                      onChange={e => {
                                        rentalCar.carType = e.target.value;
                                      }}
                                      checked={rentalCar.carType === c[1]}
                                    />
                                    <RentalCarRadioP>{c[0]}</RentalCarRadioP>
                                  </RentalCarRadio>
                                ))}
                                <RentalCarRadio>
                                  <InputName
                                    data-testid={`reserve-confirm-input-rental-car-car-type-other-${i}`}
                                    type="text"
                                    value={rentalCar.carTypeOther}
                                    onChange={e => {
                                      rentalCar.carTypeOther = e.target.value;
                                    }}
                                    disabled={rentalCar.carType !== 'other'}
                                  />
                                </RentalCarRadio>
                              </TravelerRentalCarInputAreaCarType>
                            </TravelerInputArea>
                            {!_.isEmpty(store.reserveInfo.validationErrors[`carType_${i}`]) && (
                              <>
                                <Text color="danger">{store.reserveInfo.validationErrors[`carType_${i}`]}</Text>

                                <TravelerInputArea>
                                  <TravelerRentalCarLabel>その他</TravelerRentalCarLabel>
                                  <TravelerRentalCarInputAreaCarOther />
                                </TravelerInputArea>
                              </>
                            )}
                            <TravelerInputArea>
                              <Label>台数</Label>
                              <InputAreaRight>
                                <select
                                  data-testid={`reserve-confirm-input-rental-car-car-count-${i}`}
                                  value={rentalCar.carNumber}
                                  onChange={e => {
                                    rentalCar.carNumber = e.target.value;
                                  }}
                                >
                                  {_.times(8, i => (
                                    <option value={i + 1} key={`car_count_${i + 1}`}>{`${i + 1}台`}</option>
                                  ))}
                                </select>
                              </InputAreaRight>
                            </TravelerInputArea>
                            <TravelerInputArea>
                              <Label>運転者氏名（カナ）</Label>
                              <InputAreaRight>
                                <InputName
                                  data-testid={`reserve-confirm-input-rental-car-last-name-kana-${i}`}
                                  type="text"
                                  placeholder="例) ヤマダ"
                                  value={rentalCar.lastNameKana}
                                  onChange={e => {
                                    rentalCar.lastNameKana = e.target.value;
                                  }}
                                />
                                <InputName
                                  data-testid={`reserve-confirm-input-rental-car-first-name-kana-${i}`}
                                  type="text"
                                  placeholder="例) イチロウ"
                                  value={rentalCar.firstNameKana}
                                  onChange={e => {
                                    rentalCar.firstNameKana = e.target.value;
                                  }}
                                />
                              </InputAreaRight>
                            </TravelerInputArea>
                            {!_.isEmpty(store.reserveInfo.validationErrors[`rcLastNameKana_${i}`]) && (
                              <Text color="danger">
                                {store.reserveInfo.validationErrors[`rcLastNameKana_${i}`]}
                              </Text>
                            )}
                            {!_.isEmpty(store.reserveInfo.validationErrors[`rcFirstNameKana_${i}`]) && (
                              <Text color="danger">
                                {store.reserveInfo.validationErrors[`rcFirstNameKana_${i}`]}
                              </Text>
                            )}
                            <TravelerInputArea>
                              <Label>運転者電話番号</Label>
                              <InputAreaRight>
                                <Input
                                  data-testid={`reserve-confirm-input-rental-car-driver-tel-${i}`}
                                  type="text"
                                  placeholder="例) 090-1111-2222"
                                  value={toDomesticTel(rentalCar.driverTel)}
                                  onChange={e => {
                                    rentalCar.driverTel = e.target.value;
                                  }}
                                />
                              </InputAreaRight>
                            </TravelerInputArea>
                            {!_.isEmpty(store.reserveInfo.validationErrors[`rcTel_${i}`]) && (
                              <Text color="danger">{store.reserveInfo.validationErrors[`rcTel_${i}`]}</Text>
                            )}
                            <TravelerInputArea>
                              <TravelerRentalCarLabel>備考</TravelerRentalCarLabel>
                              <TravelerRentalCarInputAreaCarRemarks>
                                <textarea
                                  data-testid={`reserve-confirm-input-rental-car-remarks-${i}`}
                                  className="companion-label"
                                  value={rentalCar.remarks}
                                  onChange={e => {
                                    rentalCar.remarks = e.target.value;
                                  }}
                                />
                              </TravelerRentalCarInputAreaCarRemarks>
                            </TravelerInputArea>
                          </RentalCarOrderContent>
                        </div>
                      ))}
                      <AddRentalCarToggle
                        data-testid="reserve-confirm-input-rental-car-add"
                        onClick={handleRentalCarInfoAdd}
                      >
                        + 予約フォームを追加する
                      </AddRentalCarToggle>
                    </>
                  )}
                </ReserveConfirmTraveler>
              </ChildContent>
            </Section>
          )}
        {store.reserveInfo.user.organization?.reserveRequestMailNotificationType === 'selectable_user' ? (
          <>
            <div data-testid="reserve-confirm-input-section-mail-notification-selectable-user">
              <Subtitle data-testid="reserve-confirm-input-subtitle-mail-notification-selectable-user">
                予約依頼に関するメール通知対象者の選択
              </Subtitle>
              <ChildContent data-testid="reserve-confirm-input-content-mail-notification-selectable-user">
                <NotifiedUserSelectorTemplate
                  applicant={store.reserveInfo.applicant.user}
                  users={store.reserveInfo.notifiedUsers}
                  onAdd={_e => {
                    store.reserveInfo.notifiedUsers.push(undefined);
                  }}
                  onSelect={(i, user) => {
                    store.reserveInfo.notifiedUsers[i] = user;
                  }}
                  onRemove={(_e, i) => {
                    store.reserveInfo.notifiedUsers.splice(i, 1);
                  }}
                />
                <SmallGraySpan data-testid="reserve-confirm-input-mail-notification-note">
                  ※申請者にはチェックにかかわらず通知されます
                </SmallGraySpan>
              </ChildContent>
            </div>
            {!_.isEmpty(store.reserveInfo.validationErrors.notifiedUsers) && (
              <Text color="danger">{store.reserveInfo.validationErrors.notifiedUsers}</Text>
            )}
          </>
        ) : (
          <div data-testid="reserve-confirm-input-section-mail-notification">
            <Subtitle data-testid="reserve-confirm-input-subtitle-mail-notification">
              予約依頼に関するメール通知の有無
            </Subtitle>
            <ChildContent data-testid="reserve-confirm-input-content-mail-notification">
              <NotificationSubArea>
                <NotificationCards>
                  <Input
                    type="checkbox"
                    data-testid="reserve-confirm-input-mail-notification-checkbox"
                    checked={store.reserveInfo.allTravelersNotification}
                    onChange={e => {
                      store.reserveInfo.allTravelersNotification = e.target.checked;
                    }}
                  />
                  <span>手配内容を関係者に通知する</span>
                </NotificationCards>
                <SmallGraySpan data-testid="reserve-confirm-input-mail-notification-note">
                  ※申請者にはチェックにかかわらず通知されます
                </SmallGraySpan>
              </NotificationSubArea>
            </ChildContent>
          </div>
        )}
        <Section data-testid="reserve-confirm-input-section-application">
          <Subtitle data-testid="reserve-confirm-input-subtitle-application">申請内容</Subtitle>
          {!isSimpleRequest &&
            store.reserveInfo.user.organization?.freeeWorkflow &&
            store.reserveInfo.isApprovalRequired && (
              <div style={styles.freeeSection}>
                <p>freeeによるワークフロー連携が設定されています。</p>
                <p>申請の依頼は予約依頼完了後にfreee上にて行ってください。</p>
              </div>
            )}
          <ChildContent data-testid="reserve-confirm-input-content-application">
            {store.reserveInfo.showBakurakuWorkflowSection && (
              <>
                <TravelerInputArea>
                  <Label>
                    Bakuraku事前申請
                    {store.reserveInfo.isBakurakuWorkflowRequired && <Red>*</Red>}
                  </Label>
                  <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                    <div style={styles.bakurakuWorkflowArea}>
                      <div style={styles.bakurakuWorkflowFilterRow}>
                        <Select
                          value={bakurakuWorkflowStatus}
                          onChange={handleBakurakuWorkflowStatusChange}
                          inputProps={{
                            'data-testid': 'reserve-confirm-input-application-bakuraku-status'
                          }}
                          style={{ width: '180px' }}
                        >
                          <option value="">すべてのステータス</option>
                          {bakurakuWorkflowStatusOptions.map(status => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <AutocompleteSelect<BakurakuWorkflowRequest>
                        key={bakurakuWorkflowSelectorKey}
                        data-testid="reserve-confirm-input-application-bakuraku-selector"
                        async="FILTERING"
                        inputChangeDelay={200}
                        getOptions={loadBakurakuWorkflowRequests}
                        value={store.reserveInfo.bakurakuWorkflowRequest}
                        getOptionLabel={option => option.label}
                        getOptionCaption={option => option.caption}
                        getOptionSelected={(option, value) => option.matches(value)}
                        onChange={handleBakurakuWorkflowRequestSelect}
                        placeholder="Bakuraku申請を検索"
                        fullWidth
                      />
                      {bakurakuWorkflowLoading && (
                        <p style={styles.bakurakuWorkflowAssistText}>Bakuraku申請を読み込んでいます。</p>
                      )}
                      {store.reserveInfo.bakurakuWorkflowRequest?.caption && (
                        <p style={styles.bakurakuWorkflowAssistText} data-wovn-ignore>
                          {store.reserveInfo.bakurakuWorkflowRequest.caption}
                        </p>
                      )}
                    </div>
                  </WorkflowInputArea>
                </TravelerInputArea>
                {!_.isEmpty(validationErrors.bakurakuWorkflowRequest) && (
                  <Text color="danger">{validationErrors.bakurakuWorkflowRequest}</Text>
                )}
                {bakurakuWorkflowFetchError && <Text color="danger">{bakurakuWorkflowFetchError}</Text>}
                {!bakurakuWorkflowLoading && bakurakuWorkflowActualCount === 0 && !bakurakuWorkflowFetchError && (
                  <p style={styles.bakurakuWorkflowAssistText}>一致するBakuraku申請はありません。</p>
                )}
              </>
            )}
            {!store.reserveInfo.user.organization?.freeeWorkflow &&
              store.reserveInfo.isApprovalRequired &&
              store.reserveInfo.workflowStyle === 'department' &&
              store.reserveInfo.displayApproverList?.list.map(
                (a, i) =>
                  (i === 0 ||
                    compareApproveStage(
                      a.approve_stage,
                      store.reserveInfo.displayApproverList?.list[i - 1]?.approve_stage
                    ) > 0) && (
                    <TravelerInputArea key={`input_approver_${a.approve_stage}`}>
                      <Label>{a.approve_stage}次承認者</Label>
                      <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                        {store.reserveInfo.displayApproverList
                          ?.joinApproverByApproveStage(a.approve_stage)
                          .join(', ')}
                      </WorkflowInputArea>
                    </TravelerInputArea>
                  )
              )}
            {!isSimpleRequest && store.reserveInfo.concurExpenseEnabled && !store.reserveInfo.concurConnected && (
              <div style={styles.freeeSection}>
                <p>Concur Expense連携が設定されています。</p>
                <p>Concur連携の設定を行ってください。</p>
              </div>
            )}
            {store.reserveInfo.isDestinationBusinessTripShown &&
              (store.reserveInfo.isApprovalRequired || store.reserveInfo.isDestinationBusinessTripRequired ? (
                <>
                  <TravelerInputArea>
                    <Label>
                      出張先<Red>*</Red>
                    </Label>
                    <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                      <InputTextArea
                        id="finalDestination"
                        data-testid="reserve-confirm-input-application-final-destination"
                        value={store.reserveInfo.finalDestination}
                        onChange={e => {
                          store.reserveInfo.finalDestination = e.target.value;
                        }}
                        placeholder="(必須)出張先"
                        rows={2}
                      />
                    </WorkflowInputArea>
                  </TravelerInputArea>
                  {!_.isEmpty(store.reserveInfo.validationErrors.area) && (
                    <Text color="danger">{store.reserveInfo.validationErrors.area}</Text>
                  )}
                  {!_.isEmpty(store.reserveInfo.validationErrors.finalDestinationLength) && (
                    <Text color="danger">{store.reserveInfo.validationErrors.finalDestinationLength}</Text>
                  )}
                </>
              ) : (
                <>
                  <TravelerInputArea>
                    <Label>出張先</Label>
                    <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                      <InputTextArea
                        id="finalDestination"
                        data-testid="reserve-confirm-input-application-final-destination"
                        value={store.reserveInfo.finalDestination}
                        onChange={e => {
                          store.reserveInfo.finalDestination = e.target.value;
                        }}
                        placeholder="(任意)出張先"
                        rows={2}
                      />
                    </WorkflowInputArea>
                  </TravelerInputArea>
                  {!_.isEmpty(store.reserveInfo.validationErrors.finalDestinationLength) && (
                    <Text color="danger">{store.reserveInfo.validationErrors.finalDestinationLength}</Text>
                  )}
                </>
              ))}
            {store.reserveInfo.isPurposeBusinessTripShown &&
              (store.reserveInfo.isApprovalRequired || store.reserveInfo.isPurposeBusinessTripRequired ? (
                <>
                  <TravelerInputArea>
                    <Label>
                      出張の目的<Red>*</Red>
                    </Label>
                    <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                      <InputTextArea
                        id="purpose"
                        data-testid="reserve-confirm-input-application-purpose"
                        value={store.reserveInfo.purpose}
                        onChange={e => {
                          store.reserveInfo.purpose = e.target.value;
                        }}
                        placeholder="(必須)出張の目的や承認者への備考などを入力してください"
                        rows={3}
                      />
                    </WorkflowInputArea>
                  </TravelerInputArea>
                  {!_.isEmpty(store.reserveInfo.validationErrors.purpose) && (
                    <Text color="danger">{store.reserveInfo.validationErrors.purpose}</Text>
                  )}
                </>
              ) : (
                <TravelerInputArea>
                  <Label>出張の目的</Label>
                  <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                    <InputTextArea
                      id="purpose"
                      data-testid="reserve-confirm-input-application-purpose"
                      value={store.reserveInfo.purpose}
                      onChange={e => {
                        store.reserveInfo.purpose = e.target.value;
                      }}
                      placeholder="(任意)出張の目的や承認者への備考などを入力してください"
                      rows={3}
                    />
                  </WorkflowInputArea>
                </TravelerInputArea>
              ))}
            {store.reserveInfo.isInternalNumberShown &&
              (store.reserveInfo.isInternalNumberRequired ? (
                <>
                  <TravelerInputArea>
                    <Label>
                      社内管理番号<Red>*</Red>
                    </Label>
                    <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                      <InputInternalNumber
                        type="text"
                        placeholder="(必須)社内稟議番号など"
                        data-testid="reserve-confirm-input-application-internal-number"
                        value={store.reserveInfo.internalNumber}
                        onChange={e => {
                          store.reserveInfo.internalNumber = e.target.value;
                        }}
                      />
                    </WorkflowInputArea>
                  </TravelerInputArea>
                  {!_.isEmpty(store.reserveInfo.validationErrors.internalNumber) && (
                    <Text color="danger">{store.reserveInfo.validationErrors.internalNumber}</Text>
                  )}
                </>
              ) : (
                <TravelerInputArea>
                  <Label>社内管理番号</Label>
                  <WorkflowInputArea workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                    <InputInternalNumber
                      type="text"
                      placeholder="(任意)社内稟議番号など"
                      data-testid="reserve-confirm-input-application-internal-number"
                      value={store.reserveInfo.internalNumber}
                      onChange={e => {
                        store.reserveInfo.internalNumber = e.target.value;
                      }}
                    />
                  </WorkflowInputArea>
                </TravelerInputArea>
              ))}
            {store.reserveInfo.isChargingDepartmentShown && (
              <>
                <TravelerInputArea>
                  <ChargingDepartmentSection
                    reserveInfo={store.reserveInfo}
                    workflowFormWidth={WORKFLOW_FORM_WIDTH}
                  />
                </TravelerInputArea>
                <ValidateMessageArea>
                  {!_.isEmpty(store.reserveInfo.validationErrors.chargingDepartment) && (
                    <Text color="danger">{store.reserveInfo.validationErrors.chargingDepartment}</Text>
                  )}
                </ValidateMessageArea>
              </>
            )}
            {store.reserveInfo.isProjectNameShown && (
              <>
                <TravelerInputArea>
                  <ProjectNameSection reserveInfo={store.reserveInfo} workflowFormWidth={WORKFLOW_FORM_WIDTH} />
                </TravelerInputArea>
                <ValidateMessageArea>
                  {!_.isEmpty(store.reserveInfo.validationErrors.projectId) && (
                    <Text color="danger">{store.reserveInfo.validationErrors.projectId}</Text>
                  )}
                </ValidateMessageArea>
              </>
            )}

            {!isSimpleRequest &&
              store.reserveInfo.expensesAccountTypeAvailable &&
              store.reserveInfo.isExpensesAcountTypeShown && (
                <>
                  <TravelerInputArea>
                    <ExpensesAccountTypeSection
                      reserveInfo={store.reserveInfo}
                      workflowFormWidth={WORKFLOW_FORM_WIDTH}
                    />
                  </TravelerInputArea>
                  <ValidateMessageArea>
                    {!_.isEmpty(store.reserveInfo.validationErrors.expensesAccountType) && (
                      <Text color="danger">{store.reserveInfo.validationErrors.expensesAccountType}</Text>
                    )}
                  </ValidateMessageArea>
                </>
              )}

            {/* ここは簡易申請でも表示させたい */}
            {store.reserveInfo.approveItems.map((d: ApproveItem) => (
              <div
                key={`approval_items_${d.id}`}
                data-testid={`reserve-confirm-input-application-custom-item-${d.id}`}
              >
                {d.dataType === 'label' ? (
                  <TravelerInputArea key={d.id}>
                    <Label data-testid={`reserve-confirm-input-application-custom-item-label-${d.id}`}>
                      <Markdown markdownText={d.userDisplayName} openLinksNewTab />
                    </Label>
                    {d.description && (
                      <WorkflowInputArea data-wovn-ignore workflowFormWidth={WORKFLOW_FORM_WIDTH}>
                        <div style={approveItemDescriptionCodeBlockStyle}>{d.description}</div>
                      </WorkflowInputArea>
                    )}
                  </TravelerInputArea>
                ) : (
                  <>
                    <TravelerInputArea key={d.id}>
                      <Label>
                        {d.userDisplayName}
                        {(d.requiredType === 'required' ||
                          store.reserveInfo.isApproveItemRequiredWithWorkflow(d.requiredType)) && <Red>*</Red>}
                      </Label>
                      {d.dataType === 'freetext' && (
                        <WorkflowInputArea
                          data-wovn-ignore
                          workflowFormWidth={WORKFLOW_FORM_WIDTH}
                          style={d.description ? approveItemInputAreaWithDescriptionStyle : undefined}
                        >
                          <InputTextArea
                            id={`${d.id}`}
                            data-testid={`reserve-confirm-input-application-custom-item-input-freetext-${d.id}`}
                            value={store.reserveInfo.approveItemValues.get(d.id)}
                            onChange={e => store.reserveInfo.approveItemValues.set(d.id, e.target.value)}
                            placeholder={d.getReservePlaceholder(
                              store.reserveInfo.isApproveItemRequiredWithWorkflow(d.requiredType)
                            )}
                            rows={1}
                          />
                          {d.description && (
                            <div style={approveItemDescriptionCodeBlockStyle}>{d.description}</div>
                          )}
                        </WorkflowInputArea>
                      )}
                      {d.dataType === 'multilinefreetext' && (
                        <WorkflowInputArea
                          data-wovn-ignore
                          workflowFormWidth={WORKFLOW_FORM_WIDTH}
                          style={d.description ? approveItemInputAreaWithDescriptionStyle : undefined}
                        >
                          <InputTextArea
                            id={`${d.id}`}
                            data-testid={`reserve-confirm-input-application-custom-item-input-multilinefreetext-${d.id}`}
                            value={store.reserveInfo.approveItemValues.get(d.id)}
                            onChange={e => store.reserveInfo.approveItemValues.set(d.id, e.target.value)}
                            placeholder={d.getReservePlaceholder(
                              store.reserveInfo.isApproveItemRequiredWithWorkflow(d.requiredType)
                            )}
                            rows={5}
                          />
                          {d.description && (
                            <div style={approveItemDescriptionCodeBlockStyle}>{d.description}</div>
                          )}
                        </WorkflowInputArea>
                      )}
                      {d.dataType === 'calendar' && (
                        <ApproveItemDatePicker
                          data-wovn-ignore
                          style={d.description ? approveItemInputAreaWithDescriptionStyle : undefined}
                        >
                          <div
                            data-testid={`reserve-confirm-input-application-custom-item-input-calendar-${d.id}`}
                          >
                            <Datepicker
                              placeholder="選択してください"
                              onChange={handleApproveCalendarSelect(d.id)}
                              dataTestIdPrefix={`reserve-confirm-input-application-custom-item-input-calendar-${d.id}`}
                              value={
                                store.reserveInfo.approveItemValues.get(d.id)
                                  ? moment(store.reserveInfo.approveItemValues.get(d.id), 'YYYY-MM-DD').toDate()
                                  : undefined
                              }
                              type="date"
                            />
                          </div>
                          {d.description && (
                            <div style={approveItemDescriptionCodeBlockStyle}>{d.description}</div>
                          )}
                        </ApproveItemDatePicker>
                      )}
                      {d.dataType === 'list' && (
                        <WorkflowInputArea
                          data-wovn-ignore
                          workflowFormWidth={WORKFLOW_FORM_WIDTH}
                          style={d.description ? approveItemInputAreaWithDescriptionStyle : undefined}
                        >
                          <div data-testid={`reserve-confirm-input-application-custom-item-input-list-${d.id}`}>
                            <SelectApproveItemList
                              onSelect={handleApproveListChange(d.id)}
                              approveItemId={d.id}
                              chargingDepartmentIds={store.reserveInfo.chargingDepartmentIDs}
                              defaultItemListName={
                                store.reserveInfo.approveItemValueCodes.get(d.id) &&
                                store.reserveInfo.approveItemValues.get(d.id)
                                  ? `${store.reserveInfo.approveItemValueCodes.get(
                                      d.id
                                    )}：${store.reserveInfo.approveItemValues.get(d.id)}`
                                  : '選択してください'
                              }
                              workflowFormWidth={WORKFLOW_FORM_WIDTH}
                            />
                          </div>
                          {d.description && (
                            <div style={approveItemDescriptionCodeBlockStyle}>{d.description}</div>
                          )}
                        </WorkflowInputArea>
                      )}
                      {d.dataType === 'file' && (
                        <WorkflowInputArea
                          data-wovn-ignore
                          workflowFormWidth={WORKFLOW_FORM_WIDTH}
                          style={d.description ? approveItemInputAreaWithDescriptionStyle : undefined}
                        >
                          <div data-testid={`reserve-confirm-input-application-custom-item-input-file-${d.id}`}>
                            <ApproveItemDropzone
                              preview={
                                store.reserveInfo.approveItemFiles.get(d.id)
                                  ? store.reserveInfo.approveItemFiles.get(d.id)?.name
                                  : undefined
                              }
                              onSelect={handleApproveFileChange(d.id)}
                              onRemove={handleApproveFileRemove(d.id)}
                            />
                            {store.reserveInfo.approveItemFiles.get(d.id) && (
                              <ApproveItemFilePreview
                                previewUrl={store.reserveInfo.approveItemFilesBase64.get(d.id) || ''}
                                fileType={store.reserveInfo.approveItemFiles.get(d.id)?.type || ''}
                                fileName={store.reserveInfo.approveItemFiles.get(d.id)?.name || ''}
                              />
                            )}
                          </div>
                          {d.description && (
                            <div style={approveItemDescriptionCodeBlockStyle}>{d.description}</div>
                          )}
                        </WorkflowInputArea>
                      )}
                    </TravelerInputArea>
                    {!_.isEmpty(store.reserveInfo.validationErrors[`appleveItem_${d.id}`]) && (
                      <Text key={`appleveItem_${d.id}`} color="danger">
                        {store.reserveInfo.validationErrors[`appleveItem_${d.id}`]}
                      </Text>
                    )}
                  </>
                )}
              </div>
            ))}
          </ChildContent>
        </Section>
        {!store.reserveInfo.user.organization?.freeeWorkflow &&
          store.reserveInfo.isApprovalRequired &&
          store.reserveInfo.workflowStyle === 'trip' && (
            <Section data-testid="reserve-confirm-input-section-workflow-route">
              {/* 旅程ごとにワークフローを通したいニーズに対応、adminでこ顧客ごとに切り替えることができる */}
              <Subtitle data-testid="reserve-confirm-input-subtitle-workflow-route">
                ワークフロー 承認ルート登録
              </Subtitle>
              <ChildContent data-testid="reserve-confirm-input-content-workflow-route">
                <ApproveRoute
                  handleApproveStagesChange={stages => {
                    store.reserveInfo.approveStages = stages;
                  }}
                  stages={store.reserveInfo.approveStages}
                />
                {!_.isEmpty(store.reserveInfo.validationErrors.approveStages) && (
                  <Text color="danger">{store.reserveInfo.validationErrors.approveStages}</Text>
                )}
              </ChildContent>
            </Section>
          )}
        {!isSimpleRequest && store.reserveInfo.isPrivateUseShown && (
          <Section data-testid="reserve-confirm-input-section-welfare">
            <Subtitle data-testid="reserve-confirm-input-subtitle-welfare">福利厚生</Subtitle>
            <ChildContent data-testid="reserve-confirm-input-content-welfare">
              <WelfareSection reserveInfo={store.reserveInfo} />
            </ChildContent>
          </Section>
        )}
        {serviceId === 1 && store.reserveInfo.reservingTrip.items.length > 0 && (
          <Section data-testid="reserve-confirm-input-section-payment">
            <Subtitle data-testid="reserve-confirm-input-subtitle-payment">支払い方法</Subtitle>
            <ChildContent data-testid="reserve-confirm-input-content-payment">
              <PaymentSection reserveInfo={store.reserveInfo} />
            </ChildContent>
          </Section>
        )}
        <ChildWrapper>
          <Section>
            {!isSimpleRequest && requiresFreeeWorkflowConnection && !store.paymentLoading && (
              <div data-testid="reserve-confirm-input-freee-workflow-note">
                <p>
                  freeeのワークフロー機能が有効になっております。
                  <Link href="/users/edit" isExternal target="_blank" rel="noopener noreferrer">
                    こちらのページ
                  </Link>
                  から、ワークフロー連携の設定を行ってください。
                </p>
              </div>
            )}
            {!isSimpleRequest && store.reserveInfo.concurExpenseEnabled && !store.reserveInfo.concurConnected && (
              <div data-testid="rc-concur-connection-note">
                <p>
                  Concur Expense連携が設定されております。
                  <Link href="/users/edit" isExternal target="_blank" rel="noopener noreferrer">
                    こちらのページ
                  </Link>
                  から、Concur連携の設定を行ってください。
                </p>
              </div>
            )}
          </Section>
        </ChildWrapper>
      </ChildWrapper>
      <div>
        {_.entries(store.reserveInfo.validationErrors).map(([k, v]) => (
          <Text key={k} color="danger">
            {v?.split('\n').map((line: string) => (
              <React.Fragment key={line}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </Text>
        ))}
      </div>
      <div>
        {store.paymentLoading ? (
          <PaymentLoadingImg src="/images/loading.gif" width={50} height={50} />
        ) : (
          <ButtonArea>
            {!isSimpleRequest && (
              <ConfirmBack
                data-testid="reserve-confirm-input-back-button"
                onClick={() => handleBackToSelectClick()}
              >
                {'< 戻る'}
              </ConfirmBack>
            )}
            <ConfirmButtons>
              {handleSaveDraft && <DraftButton onClick={handleSaveDraft}>下書き保存</DraftButton>}
              {!isSimpleRequest &&
                !requiresFreeeWorkflowConnection &&
                !(store.reserveInfo.concurExpenseEnabled && !store.reserveInfo.concurConnected) && (
                  <NextButton to="step2" onClick={validateInput} data-testid="reserve-confirm-input-next-button">
                    次へ
                  </NextButton>
                )}
            </ConfirmButtons>
          </ButtonArea>
        )}
      </div>
    </StylesContext.Provider>
  );
};

type DivProps = React.HTMLAttributes<HTMLDivElement>;

const Title: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.title, ...style }}>
      {children}
    </div>
  );
};

const ChildWrapper: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.childWrapper, ...style }}>
      {children}
    </div>
  );
};

const ChildContent: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.childContent, ...style }}>
      {children}
    </div>
  );
};

const Section: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.section, ...style }}>
      {children}
    </div>
  );
};

const Subtitle: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.subtitle, ...style }}>
      {children}
    </div>
  );
};

const SubtitleLeft: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.subtitleLeft, ...style }}>
      {children}
    </div>
  );
};

const SubtitleRight: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.subtitleRight, ...style }}>
      {children}
    </div>
  );
};

const Label: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.label, ...style }}>
      {children}
    </div>
  );
};

const GrayLabel: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.grayLabel, ...style }}>
      {children}
    </div>
  );
};

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = props => {
  const styles = useStylesContext();
  return <input {...props} style={{ ...styles.input, ...props.style }} />;
};

const InputInternalNumber: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = props => {
  const styles = useStylesContext();
  return <input {...props} style={{ ...styles.inputInternalNumber, ...props.style }} />;
};

const InputTextArea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = props => {
  const styles = useStylesContext();
  return <textarea {...props} style={{ ...styles.inputTextArea, ...props.style }} />;
};

const InputAreaRight: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.inputAreaRight, ...style }}>
      {children}
    </div>
  );
};

const InputNationarityAreaRight: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.inputNationarityAreaRight, ...style }}>
      {children}
    </div>
  );
};

const InputAirTicketAreaRight: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.inputAirTicketAreaRight, ...style }}>
      {children}
    </div>
  );
};

const InputBirthDay: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = props => {
  const styles = useStylesContext();
  return <input {...props} style={{ ...styles.inputBirthDay, ...props.style }} />;
};

const InputName: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = props => {
  const styles = useStylesContext();
  return (
    <input
      {...props}
      style={{
        ...styles.inputName,
        ...(props.disabled ? styles.inputNameDisabled : null),
        ...props.style
      }}
    />
  );
};

const SmallGraySpan: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.smallGraySpan, ...props.style }}>
      {children}
    </div>
  );
};

const ReserveConfirmTraveler: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.reserveConfirmTraveler, ...props.style }}>
      {children}
    </div>
  );
};

const PeopleNum: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <p style={styles.peopleNum}>{children}</p>;
};

const TravelerInputArea: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerInputArea}>{children}</div>;
};

const ValidateMessageArea: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.validateMessageArea}>{children}</div>;
};

const RailwayTravelerInputHead: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.railwayTravelerInputHead}>{children}</div>;
};

const RailwayTravelerInputBox: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.railwayTravelerInputBox}>{children}</div>;
};

const RailwayTravelerInputArea: React.FC<{ withBorder: boolean }> = ({ children, withBorder }) => {
  const styles = useStylesContext();
  return (
    <div
      style={{
        ...styles.railwayTravelerInputArea,
        ...(withBorder ? styles.railwayTravelerInputAreaWithBorder : null)
      }}
    >
      {children}
    </div>
  );
};

const TravelerInputAreaItem: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerInputAreaItem}>{children}</div>;
};

const RailwayTravelerInputName: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.railwayTravelerInputName, ...props.style }}>
      {children}
    </div>
  );
};

const ThePersonIcon: React.FC = () => {
  const styles = useStylesContext();
  return <PersonIcon style={styles.personIcon} />;
};

const TravelerPeopleNum: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <p {...props} style={{ ...styles.travelerPeopleNum, ...props.style }}>
      {children}
    </p>
  );
};

const TravelerType: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.travelerType, ...props.style }}>
      {children}
    </label>
  );
};

const TravelerRentalCarLabel: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerRentalCarLabel}>{children}</div>;
};

const RentalCarDescription: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <p style={styles.rentalCarDescription}>{children}</p>;
};

const RentalCarLabel: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.rentalCarLabel, ...props.style }}>
      {children}
    </label>
  );
};

const RentalCarRadio: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.rentalCarRadio, ...props.style }}>
      {children}
    </label>
  );
};

const RentalCarRadioP: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return (
    <p className="reserve-confirm-input-customer-info__rental-car-radio-p" style={styles.rentalCarRadioP}>
      {children}
    </p>
  );
};

const RentalCarOrderTitle: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <p style={styles.rentalCarOrderTitle}>{children}</p>;
};

const RentalCarOrderContent: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.rentalCarOrderContent}>{children}</div>;
};

const RentalCarDatePicker: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.rentalCarDatePicker}>{children}</div>;
};

const ApproveItemDatePicker: React.FC<React.HTMLAttributes<HTMLDivElement> & { workflowFormWidth?: string }> = ({
  children,
  workflowFormWidth,
  ...props
}) => {
  const styles = useStylesContext();
  return (
    <div
      {...props}
      style={{ ...styles.approveItemDatePicker, width: workflowFormWidth || WORKFLOW_FORM_WIDTH, ...props.style }}
    >
      {children}
    </div>
  );
};

const RentalCarRemove: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.rentalCarRemove, ...props.style }}>
      {children}
    </div>
  );
};

const AddRentalCarToggle: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.addRentalCarToggle, ...props.style }}>
      {children}
    </div>
  );
};

const TravelerRentalCarInputArea: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerRentalCarInputArea}>{children}</div>;
};

const TravelerRentalCarInputAreaTitleText: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerRentalCarInputAreaTitleText}>{children}</div>;
};

const TravelerRentalCarInputAreaTextInput: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return (
    <div
      className="reserve-confirm-input-customer-info__traveler-rental-car-input-area-text-input"
      style={styles.travelerRentalCarInputAreaTextInput}
    >
      {children}
    </div>
  );
};

const TravelerRentalCarInputAreaCarType: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return (
    <div
      className="reserve-confirm-input-customer-info__traveler-rental-car-input-area-car-type"
      style={styles.travelerRentalCarInputAreaCarType}
    >
      {children}
    </div>
  );
};

const TravelerRentalCarInputAreaCarOther: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerRentalCarInputAreaCarOther}>{children}</div>;
};

const TravelerRentalCarInputAreaCarRemarks: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerRentalCarInputAreaCarRemarks}>{children}</div>;
};

const RadioLabel: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.radioLabel, ...props.style }}>
      {children}
    </label>
  );
};

const TheRadio: React.FC<React.ComponentProps<typeof Radio>> = props => {
  const styles = useStylesContext();
  return (
    <Radio
      {...props}
      className={`reserve-confirm-input-customer-info__radio ${props.className || ''}`}
      style={{ ...styles.theRadio, ...props.style }}
    />
  );
};

const BusinessEkinetTicketRadio: React.FC<{
  disabled: boolean;
  disabledTooltip: string;
  dataTestId: string;
}> = ({ disabled, disabledTooltip, dataTestId }) => {
  const { theme } = useAITTheme();
  const [showTooltip, setShowTooltip] = useState(false);

  if (!disabled) {
    return (
      <TheRadio
        value="business_ekinet"
        radioInputProps={{
          'data-testid': dataTestId
        }}
      >
        ビジネスえきねっと
      </TheRadio>
    );
  }

  return (
    <span
      style={{ display: 'inline-flex', position: 'relative', cursor: 'not-allowed' }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      data-testid={`${dataTestId}-disabled-wrap`}
    >
      <span style={{ display: 'inline-flex', pointerEvents: 'none', opacity: 0.5 }}>
        <TheRadio
          value="business_ekinet"
          disabled
          radioInputProps={{
            'data-testid': dataTestId
          }}
        >
          ビジネスえきねっと
        </TheRadio>
      </span>
      {showTooltip && (
        <span
          role="tooltip"
          data-testid={`${dataTestId}-tooltip`}
          style={{
            position: 'absolute',
            top: '110%',
            left: 0,
            zIndex: 10,
            minWidth: 140,
            padding: 5,
            borderRadius: 3,
            border: `1px solid ${theme.tokens.colors.border.field}`,
            background: theme.tokens.colors.background.primary,
            color: theme.tokens.colors.text.primary,
            fontSize: 12,
            fontWeight: 'normal',
            whiteSpace: 'nowrap',
            lineHeight: 1.4
          }}
        >
          {disabledTooltip}
        </span>
      )}
    </span>
  );
};

const FlexRadio: React.FC<React.ComponentProps<typeof Radio>> = props => {
  return (
    <TheRadio {...props} className={`reserve-confirm-input-customer-info__flex-radio ${props.className || ''}`} />
  );
};

const Red: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <span style={styles.red}>{children}</span>;
};

const ButtonArea: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.buttonArea}>{children}</div>;
};

const ConfirmBack: React.FC<React.ComponentProps<typeof Link>> = props => {
  const styles = useStylesContext();
  return <Link {...props} style={{ ...styles.confirmBack, ...(props.style || {}) }} />;
};

const NextButton: React.FC<React.ComponentProps<typeof LinkButton>> = props => {
  const styles = useStylesContext();
  return <LinkButton {...props} style={{ ...styles.nextButton, ...(props.style || {}) }} />;
};

const NotificationSubArea: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.notificationSubArea}>{children}</div>;
};

const NotificationCards: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.notificationCards, ...props.style }}>
      {children}
    </label>
  );
};

const HotelName: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return (
    <p className="reserve-confirm-input-customer-info__hotel-name" style={styles.hotelName}>
      {children}
    </p>
  );
};

const RoomNum: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return (
    <p className="reserve-confirm-input-customer-info__room-num" style={styles.roomNum}>
      {children}
    </p>
  );
};

const SelectTravelersSelectedListItem: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.selectTravelersSelectedListItem}>{children}</div>;
};

const MembersListItemInfo: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.membersListItemInfo}>{children}</div>;
};

const MembersListItemName: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <p style={styles.membersListItemName}>{children}</p>;
};

const FlightCustomerInfo: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.flightCustomerInfo, ...style }}>
      {children}
    </div>
  );
};

const CaptionText: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <p {...rest} style={{ ...styles.captionText, ...style }}>
      {children}
    </p>
  );
};

const StyledAutoCompletableInput: React.FC<React.ComponentProps<typeof AutoCompletableInput>> = props => {
  return (
    <AutoCompletableInput
      {...props}
      className={`reserve-confirm-input-customer-info__auto-completable-input ${props.className || ''}`.trim()}
    />
  );
};

const AutoCompletableInputBox: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.autoCompletableInputBox, ...style }}>
      {children}
    </div>
  );
};

const WorkflowInputArea: React.FC<React.HTMLAttributes<HTMLDivElement> & { workflowFormWidth?: string }> = ({
  children,
  workflowFormWidth,
  ...props
}) => {
  const styles = useStylesContext();
  return (
    <div
      {...props}
      style={{ ...styles.workflowInputArea, width: workflowFormWidth || WORKFLOW_FORM_WIDTH, ...props.style }}
    >
      {children}
    </div>
  );
};

const ConfirmButtons: React.FC<DivProps> = ({ children, style, ...rest }) => {
  const styles = useStylesContext();
  return (
    <div {...rest} style={{ ...styles.confirmButtons, ...style }}>
      {children}
    </div>
  );
};

const DraftButton: React.FC<React.ComponentProps<typeof Button>> = props => {
  return <Button color="sub" {...props} />;
};

const PaymentLoadingImg: React.FC<React.ComponentProps<'img'>> = props => {
  const styles = useStylesContext();
  return <img {...props} style={{ ...styles.paymentLoadingImg, ...props.style }} />;
};

const MileageCaptionText: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.mileageCaptionText}>{children}</div>;
};

function getStyles(theme: ReturnType<typeof useAITTheme>['theme']) {
  return {
    title: {
      marginBottom: 10,
      fontSize: 18,
      fontWeight: 'bold' as const,
      paddingBottom: 10
    },
    childWrapper: {
      fontSize: 13,
      marginBottom: 20
    },
    childContent: {
      fontSize: 13,
      marginLeft: 10
    },
    section: {
      paddingBottom: 20
    },
    subtitle: {
      fontSize: 15,
      margin: '20px 0 10px',
      fontWeight: 'bold' as const
    },
    subtitleLeft: {
      float: 'left' as const,
      paddingRight: 5
    },
    subtitleRight: {
      float: 'left' as const,
      fontSize: 13,
      fontWeight: 'normal' as const
    },
    label: {
      fontSize: 13,
      width: 200,
      lineHeight: '34px',
      wordWrap: 'break-word' as const,
      paddingRight: 5
    },
    grayLabel: {
      color: theme.tokens.colors.text.description,
      fontSize: 13,
      lineHeight: '34px',
      fontWeight: 'bold' as const,
      wordWrap: 'break-word' as const
    },
    input: {
      maxWidth: 300,
      marginRight: 10
    },
    inputInternalNumber: {
      marginRight: 0
    },
    inputTextArea: {
      marginRight: 0
    },
    inputAreaRight: {
      lineHeight: '34px',
      display: 'flex',
      width: WORKFLOW_FORM_WIDTH
    },
    inputNationarityAreaRight: {
      lineHeight: '34px',
      display: 'flex',
      width: WORKFLOW_FORM_WIDTH,
      marginBottom: 10
    },
    inputAirTicketAreaRight: {
      lineHeight: '34px',
      display: 'flex',
      flexFlow: 'column' as const,
      width: WORKFLOW_FORM_WIDTH
    },
    inputBirthDay: {
      width: 120,
      marginRight: 10
    },
    inputName: {
      width: 120,
      marginRight: 10
    },
    inputNameDisabled: {
      backgroundColor: theme.tokens.colors.background.disabled
    },
    smallGraySpan: {
      fontSize: 11,
      color: theme.tokens.colors.text.description
    },
    reserveConfirmTraveler: {
      marginBottom: 15,
      marginLeft: 10
    },
    peopleNum: {
      fontSize: 13,
      marginTop: 10
    },
    travelerInputArea: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      marginBottom: 5
    },
    validateMessageArea: {
      marginBottom: 5
    },
    railwayTravelerInputHead: {
      padding: '0 12px 12px'
    },
    railwayTravelerInputBox: {
      marginBottom: 24,
      padding: '12px 0',
      backgroundColor: theme.tokens.colors.background.primary
    },
    railwayTravelerInputArea: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      marginBottom: 5,
      padding: '0 20px'
    },
    railwayTravelerInputAreaWithBorder: {
      borderTop: `1px solid ${theme.tokens.colors.border.divider}`
    },
    travelerInputAreaItem: {
      margin: '3px 0 10px'
    },
    railwayTravelerInputName: {
      paddingRight: 10,
      fontWeight: 'bold' as const
    },
    personIcon: {
      color: theme.tokens.colors.text.description,
      verticalAlign: 'middle',
      marginRight: 5
    },
    travelerPeopleNum: {
      fontSize: 13
    },
    travelerType: {
      margin: '10px 0 5px',
      fontSize: 13
    },
    travelerRentalCarLabel: {
      lineHeight: '34px',
      display: 'flex',
      float: 'left' as const
    },
    rentalCarDescription: {
      clear: 'both' as const,
      padding: '10px 0'
    },
    rentalCarLabel: {
      margin: '10px 0 5px',
      fontSize: 13
    },
    rentalCarRadio: {
      marginLeft: 20
    },
    rentalCarRadioP: {
      display: 'inline-block'
    },
    rentalCarOrderTitle: {
      margin: '10px 0 5px'
    },
    rentalCarOrderContent: {
      marginLeft: 0,
      paddingLeft: 20,
      position: 'relative' as const,
      borderLeft: `3px solid ${theme.tokens.colors.border.field}`
    },
    rentalCarDatePicker: {
      marginLeft: 0,
      display: 'block'
    },
    approveItemDatePicker: {
      marginLeft: 0,
      display: 'block'
    },
    rentalCarRemove: {
      float: 'right' as const,
      cursor: 'pointer',
      marginRight: 60
    },
    addRentalCarToggle: {
      color: theme.tokens.colors.brand.primary,
      textDecoration: 'none',
      transition: 'color 0.1s linear',
      cursor: 'pointer'
    },
    travelerRentalCarInputArea: {
      lineHeight: '34px'
    },
    travelerRentalCarInputAreaTitleText: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      marginBottom: 5
    },
    travelerRentalCarInputAreaTextInput: {
      lineHeight: '34px',
      marginLeft: 20,
      marginTop: 5
    },
    autoCompletableInputBox: {
      border: `1px solid ${theme.tokens.colors.border.field}`,
      borderRadius: 4,
      padding: '0.5em',
      height: 'auto',
      width: 300
    },
    travelerRentalCarInputAreaCarType: {
      lineHeight: '34px',
      marginLeft: 52
    },
    travelerRentalCarInputAreaCarOther: {
      lineHeight: '34px',
      marginLeft: 13
    },
    travelerRentalCarInputAreaCarRemarks: {
      lineHeight: '34px',
      marginTop: 10,
      marginLeft: 47
    },
    radioLabel: {
      display: 'flex',
      marginRight: 10
    },
    theRadio: {
      marginRight: 10
    },
    red: {
      color: theme.tokens.colors.danger.primary
    },
    buttonArea: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    confirmBack: {
      margin: 'auto 0'
    },
    nextButton: {
      marginLeft: 15,
      padding: '0.75em 1em',
      lineHeight: 1
    },
    notificationSubArea: {
      marginTop: 15
    },
    notificationCards: {
      marginBottom: 15
    },
    hotelName: {
      fontSize: 13,
      marginTop: 10,
      marginBottom: 5,
      fontWeight: 'bold' as const
    },
    roomNum: {},
    selectTravelersSelectedListItem: {
      display: 'flex',
      padding: '5px 20px',
      border: `2px solid ${theme.tokens.colors.border.divider}`,
      cursor: 'pointer'
    },
    membersListItemInfo: {
      display: 'flex',
      flexWrap: 'wrap' as const
    },
    membersListItemName: {
      marginRight: 10
    },
    flightCustomerInfo: {
      display: 'flex'
    },
    captionText: {
      lineHeight: 1.5,
      color: theme.tokens.colors.text.primary,
      fontSize: 11,
      marginTop: 0,
      marginBottom: 0,
      letterSpacing: '0.007em'
    },
    freeeSection: {
      marginBottom: 10
    },
    workflowInputArea: {
      lineHeight: '34px',
      display: 'flex'
    },
    bakurakuWorkflowArea: {
      width: '100%'
    },
    bakurakuWorkflowFilterRow: {
      marginBottom: 8
    },
    bakurakuWorkflowAssistText: {
      marginTop: 8,
      fontSize: 12,
      lineHeight: '18px',
      color: theme.tokens.colors.text.description
    },
    confirmButtons: {
      display: 'flex'
    },
    paymentLoadingImg: {
      display: 'block',
      margin: '0 auto'
    },
    mileageCaptionText: {
      fontSize: 11,
      letterSpacing: '0.02em'
    }
  };
}

const bedTypeWarningStyle = {
  fontSize: '0.9em',
  marginBottom: '8px'
} as const;

const renderBedTypeWarning = (bedTypes: HotelBedType[] | null | undefined) => {
  if (!bedTypes || bedTypes.length <= 1) {
    return null;
  }

  const bedTypesText = bedTypes.map(bedType => bedType.description).join('、または');
  return (
    <p style={bedTypeWarningStyle}>
      ※本プランでは予約確定後でもベッドタイプは確約されません。
      {bedTypesText}になる可能性があります。
    </p>
  );
};

export default observer(InputCustomerInformation);
/*  eslint-enable max-lines */
